import {
  ARCHIVE_REFUSAL,
  ASSIGN_REFUSAL,
  BULK_REFUSAL,
  DELETION_REFUSAL,
  DOCTOR_PRICE_REFUSAL,
  EXCHANGE_RATE_REFUSAL,
  REDO_REFUSAL,
  SHIPPING_REFUSAL,
  SPAN_RULE,
  STAGE_LABEL,
  TAKE_OVER_REFUSAL,
  USER_ACCESS_REFUSAL,
  WORK_PROOF_REFUSAL,
  type ChangeField,
  type Role,
  type RouteAfterEdit,
  type ViewKey,
} from '@/domain';

/**
 * Every user facing string in the product, in one module. Section 13.
 *
 * English only at launch. Keeping the copy here means Arabic and right to left
 * are a later addition rather than a rewrite, and it means the character audit
 * in section 14.3 has one place to check rather than every JSX file.
 *
 * Two characters are banned outright and this is where they would creep in
 * first: no em dash and no arrow. Use a middle dot, a comma, or the word.
 */

export const strings = {
  app: {
    name: 'Ceramica Dental Lab',
    tagline: 'Production System',
  },

  login: {
    usernameLabel: 'Username',
    usernamePlaceholder: 'e.g. dana',
    passwordLabel: 'Password',
    passwordPlaceholder: 'password',
    submit: 'Sign in',
    submitting: 'Signing in',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    demoHint: 'Demo password for every account: ceramica.',
    demoHintDetail: 'A real deployment uses hashed passwords, sessions and password reset.',
    accountsHeading: 'Or pick a seed account to sign in',
    usernameRequired: 'Enter your user name',
    passwordRequired: 'Enter your password',
    remember: 'Remember me',
    unknownUser: 'Username or password is incorrect.',
    // Section 4.2 and the client's pre-deployment request. The station roles
    // sign in inside the lab; everybody else works at a desk and travels.
    geofenceOff: 'Station sign-in is not restricted by location',
    outsideLab: 'Accessing the system is only allowed in the lab.',
    locationUnavailable:
      'Location is switched off, so station sign-in cannot be allowed. Turn on location for this site and try again.',
  },

  // Changing your own password, from the user block on every screen and from
  // your own record. Section 4 of the request of 13 September 2026.
  password: {
    action: 'Change password',
    title: 'Change your password',
    lead: 'You stay signed in here. Every other device signed in as you is signed out.',
    current: 'Current password',
    next: 'New password',
    confirm: 'New password again',
    mismatch: 'The two new passwords are not the same',
    submit: 'Change password',
    changing: 'Changing',
    changed: 'Password changed.',
    cancel: 'Cancel',
    forcedTitle: 'Choose your own password',
    forcedLead:
      'You signed in with the password everybody was given. Choose one of your own, eight characters or more, before carrying on.',
    signOut: 'Sign out',
  },

  shell: {
    menu: 'Menu',
    /** The third tab on a technician's phone, beside the tray and the scanner. */
    profileTab: 'Profile',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    /*
     * The scrim, named apart from the menu's own close button. Two buttons
     * carrying one name read as one control to a screen reader, and the scrim
     * dismisses whichever panel is open rather than the menu in particular.
     */
    closeOverlay: 'Close the open panel',
    search: 'Search',
    notifications: 'Notifications',
    unread: 'unread',
    signOut: 'Sign out',
    scan: 'Scan',
    skipToContent: 'Skip to content',
  },

  /*
   * Loading copy. Every wait names what is being fetched rather than saying
   * "Loading", because the label is what a screen reader announces and
   * "Loading" three times on one screen tells somebody nothing about which
   * part of it is still arriving.
   */
  common: {
    loading: 'Loading',
    loadingBoard: 'Loading the production board',
    loadingOrders: 'Loading orders',
    loadingArchive: 'Loading the archive',
    loadingActivity: 'Loading activity',
    loadingUsers: 'Loading the team',
    loadingReports: 'Loading the report',
    loadingNotifications: 'Loading notifications',
    loadingCase: 'Loading the case',
    loadingKit: 'Loading the kit',
    loadingSession: 'Loading your session',
  },

  errors: {
    offline: 'Offline. Your work is saved and will sync.',
    forbidden: 'You do not have access to this action',
    conflict: 'Somebody else changed this while you were editing. Refresh and try again.',
    server: 'Something went wrong at our end.',
    // What a failure is said as when it has no sentence of its own for a person: see
    // shared/failure. Plain, and nothing of how the system is built. Request of 9 October 2026.
    unreachable: 'Could not reach the server. Check the connection and try again.',
    busy: 'The server is busy or restarting. Try again in a minute.',
    notDone: 'That could not be done. Try again.',
    sessionEnded: 'Your session has ended. Sign in again.',
    notFound: 'That is no longer there. It may have been removed.',
    tooLarge: 'That file is too large to send.',
    tooMany: 'Too many attempts. Wait a minute and try again.',
    retry: 'Try again',
    traceLabel: 'Reference',
    unexpected: 'Something went wrong on this screen.',
    unexpectedDetail: 'The rest of the application is still working. Reload to carry on.',
    reload: 'Reload',
  },

  /*
   * The queue and the tray. Section 1 of the request of 5 October 2026 fixes the
   * two words for every screen: a QUEUE is the cases assigned to one person; a
   * TRAY is any other list of cases, a department's above all. "Bench" is not
   * used anywhere a person reads; a department's list is its tray.
   *
   * (The request of 4 October 2026 had renamed this whole page "Tray". It is the
   * technician's Queue again, with their department's tray one tap away.)
   */
  /*
   * Undo, on the toast after an action. Section 6 of the request of
   * 5 October 2026. The refusals are the server's, shown as they arrive.
   */
  undo: {
    action: 'Undo',
    done: (jo: string) => `${jo} is back where it was`,
    doneMany: (count: number) =>
      `${String(count)} ${count === 1 ? 'case is' : 'cases are'} back where they were`,
  },

  /*
   * How many cases a person holds now, beside their name wherever a case is
   * given to somebody. Sections 7 and 8 of the request of 5 October 2026.
   */
  workload: {
    cases: (count: number) =>
      count === 0 ? 'no cases' : count === 1 ? '1 case' : `${String(count)} cases`,
    leastBusy: 'Least busy first',
  },

  queue: {
    held: 'On hold',
    title: 'Queue',
    myQueue: 'My queue',
    tray: 'Tray',
    /** A department's list: the department, then the word. */
    trayOf: (stage: string) => `${stage} tray`,
    queueHint: 'The cases assigned to you.',
    trayHint: 'Every case at your department. Scan one to take it.',
    filterLabel: 'My queue or the department tray',
    cases: 'Cases',
    queueEmpty: 'Nothing in your queue.',
    queueEmptyDetail: 'Scan a case to take it, or look in the tray.',
    nothingElse: 'Nothing waiting on this tray.',
    nothingElseDetail: 'Scan a case to take it or check back later.',
    loading: 'Loading your queue',
    // Search, sort and filters. Section 3 of the request of 5 October 2026.
    toolsLabel: 'Search, sort and filter',
    search: 'Search',
    searchPlaceholder: 'Job order, patient, dentist or clinic',
    filters: 'Filters',
    urgentOnly: 'Urgent only',
    status: 'Status',
    anyStatus: 'Any status',
    caseType: 'Case type',
    anyType: 'Any type',
    anyDue: 'Any due date',
    dueToday: 'Due today',
    overdue: 'Overdue',
    reset: 'Reset',
    ofTotal: (total: number) => `of ${String(total)}`,
    show: (count: number) => `Show ${String(count)} ${count === 1 ? 'case' : 'cases'}`,
    noMatch: 'No cases match.',
    noMatchDetail: 'Try another search, or reset the filters.',
    dueIn: 'Due',
    days: (count: number) => `${String(count)}d`,
    open: 'Open',
    timerRunning: 'timer running',
    timerPaused: 'paused',
    due: 'Due',
    waited: 'waited',
    tooth: 'Tooth',
    teeth: 'Teeth',
    shade: 'Shade',
    note: 'Note',
    urgent: 'Urgent',
    rework: 'Rework',
    you: 'You',
    unassigned: 'Unassigned',
    // For a row whose name is ever missing: a word, never an id.
    assigned: 'Assigned',
  },

  job: {
    heldBanner: 'On hold',
    heldNotice: 'The lab has stopped this case. Nobody works on it until the hold is released.',
    back: 'Back to queue',
    backGeneric: 'Back',
    print: 'Print sheet',
    received: 'Received',
    delivery: 'Delivery',
    deliveryAuto: 'auto',
    dueIn: 'working day',
    dueInPlural: 'working days',
    caseReference: 'Case reference',
    jobOrder: 'Job order',
    kitId: 'Kit ID',
    input: 'Input',
    restoration: 'Restoration',
    specification: 'Specification',
    translucency: 'Translucency',
    anatomy: 'Anatomy',
    block: 'Zircon block',
    stain: 'Stain',
    shadeFinish: 'Shade & finish',
    shade: 'Shade',
    incisal: 'Incisal transparency',
    occlusal: 'Occlusal stain',
    ingot: 'Ingot press',
    // Section 1 of the request of 13 September 2026: the two specification
    // fields the bench owns, edited in place on the job view.
    ingotOwnedBy: 'Recorded by Wax & Fitting',
    blockOwnedBy: 'Recorded by CAD / CAM',
    notRecorded: 'Not recorded yet',
    editField: 'Change',
    saveField: 'Save',
    cancelField: 'Cancel',
    fieldSaved: 'Saved',
    itemsIncluded: 'Items included',
    notes: 'Job notes',
    teeth: 'Teeth',
    toothUnit: 'tooth',
    toothUnits: 'teeth',
    chartNotMarked: 'chart not marked yet',
    bridges: 'Bridges & bars',
    editCase: 'Edit case',

    currentTask: 'Current task',
    // The fold on the action bar, on a phone. Request of 24 September 2026.
    actions: 'Actions',
    /*
     * Section 1 of the request of 20 September 2026. The timer is wall clock and
     * nothing stops it but Pause or Done, so "tracking active time" and
     * "paused · idle not counted" were both untrue and are gone.
     */
    resume: 'Resume',
    showTask: 'Show the task in full',
    hideTask: 'Fold the task to one line',
    fromScan: 'auto-tracked from scan',
    notYours: 'viewing only · scan the tray to take it',
    start: 'Start task',
    scanToTake: 'Scan the tray to take this case',
    notYourBench: 'This tray is at',
    notYourBenchTail: 'which is not your tray. Hand it to that station.',
    /*
     * Taking a case over. Section 7 of the request of 20 September 2026, and
     * section 9 of the request of 5 October 2026: a case at another department,
     * or one assigned to somebody else. Without the right to, the notice above
     * is all there is, and nothing is sent; with it, the notice becomes a card
     * with one button, and the confirmation is the warning.
     */
    takeOver: 'Take this case',
    takeOverTitle: 'Take',
    takeOverFrom: 'from',
    takeOverLead: 'It will move to',
    takeOverLeadTail: 'and start on your clock. Whoever holds it will be told.',
    takeOverConfirm: 'Take it and start',
    takeOverTold: 'They will be told, and it will leave their queue.',
    takeOverMoves: (to: string) => `It will move to ${to} and start on your clock.`,
    takeOverStays: 'It stays where it is and starts on your clock.',
    takeOverCancel: 'Cancel',
    takeOverHeldBy: 'Held by',
    /*
     * A case at Final Check that this person cannot take, while the lab's
     * setting is off: the message with one OK button, in the client's two
     * lines. The request of 5 October 2026 (third), section 1. Worded once in
     * the domain, because the server refuses in the same words.
     */
    finalCheckClosed: {
      title: TAKE_OVER_REFUSAL.finalCheckOff,
      ask: TAKE_OVER_REFUSAL.finalCheckAsk,
    },
    taken: 'taken to your tray',
    takenOver: 'is yours now',
    tookCase: 'taken',
    // Handing a finished case to a named person on the next bench, when the
    // route is automatic and Mark done asks nothing. Section 5.2.
    sendToSomeone: 'Send to someone at',
    sentBack: 'Sent back from Final Check',
    sentHere: 'Sent here from',
    urgentOn: 'Mark urgent',
    urgentOff: 'Not urgent any more',
    urgentReason: 'Why is it urgent?',
    urgentMarked: 'marked urgent',
    urgentCleared: 'no longer urgent',
    pause: 'Pause',
    markDone: 'Mark done',
    flag: 'Flag',
    addNote: 'Note',

    flagged: 'flagged and',
    completed: 'completed',
    logged: 'logged',
    sentTo: 'sent to',

    photosHeading: 'Case photos',
    photoFromIntake: 'From reception',
    photoGateHeading: 'Case photo (optional)',
    photoGateOpen: 'Add a photo of the finished case if you have one. It is not needed to mark the case done.',
    photoGateMet: 'photo attached.',
    photoGateMetPlural: 'photos attached.',
    photoInputLabel: 'Case photo file',
    attachPhoto: 'Take or attach photo',
    attachAnother: 'Add another photo',
  },

  /*
   * The running tasks, under the top bar on every screen. Section 2.4 of the
   * request of 20 September 2026: a technician may have three cases counting and
   * be looking at none of them.
   */
  running: {
    label: 'Cases you have running',
    one: 'Running',
    many: 'running',
    pause: 'Pause',
  },

  flag: {
    title: 'Flag',
    lead: 'Pick the department this case goes to. It stays flagged until it passes Final Check.',
    sendTo: 'Send to',
    reason: 'What is wrong',
    reasonPlaceholder: 'e.g. shade mismatch, remake the coping',
    cancel: 'Cancel',
    confirm: 'Flag and send',
    pickDepartment: 'Pick a department first',
    reasonRequired: 'Say what is wrong before sending it on',
    // The second step: who at that bench takes it. Section 5 of the request of
    // 20 September 2026.
    sendToPerson: 'And to whom',
    nobodyInParticular: 'Nobody in particular',
  },

  /*
   * Giving a case to one person. Section 1 of the request of 13 September 2026.
   * Assign is the pair of Flag, and the lead line says how the two differ.
   */
  assign: {
    action: 'Assign',
    title: 'Assign',
    lead: (stage: string) =>
      `Give the case to someone at ${stage}, or choose another department to send it there.`,
    department: 'Department',
    hereNow: 'here now',
    moveStatement: (jo: string, stage: string, person: string | null) =>
      person === null ? `Send ${jo} to ${stage}?` : `Send ${jo} to ${stage} and give it to ${person}?`,
    moved: (jo: string, stage: string, person: string | null) =>
      person === null ? `${jo} sent to ${stage}` : `${jo} sent to ${stage} for ${person}`,
    undo: 'Undo',
    undone: (jo: string, stage: string) => `${jo} is back at ${stage}`,
    who: 'Give it to',
    loading: 'Loading the team',
    unassigned: 'Leave it unassigned',
    current: 'currently',
    workingNow: (name: string) => `${name} is working on this now.`,
    confirm: 'Assign',
    cancel: 'Cancel',
    assigned: 'assigned to',
    cleared: 'is unassigned',
    // A note with the assignment, to everyone or to the person alone.
    // Request of 4 October 2026, section 7.
    note: 'Note for Lab Notes (optional)',
    notePlaceholder: 'What should they know?',
    whoSees: 'Who sees the note',
    everyone: 'Everyone',
    onlyAssignee: 'Only the assigned technician',
    onlyAssigneeHint: 'Management and supervisors can read it too.',
    forward: 'Forward earlier private notes',
    forwardHint: 'Earlier private notes stay with the person they were written to unless you forward them.',
    // The refusal, worded once in the domain and rendered verbatim.
    notInSystem: ASSIGN_REFUSAL.notInSystem,
  },

  /*
   * Proof of work: a photograph of the finished work at Mark done. Section 2 of
   * the request of 13 September 2026. The three refusals are the domain's.
   */
  proof: {
    title: 'Proof of work',
    leadOptional:
      'Take a photograph of the finished work before this case moves on, or finish without one.',
    leadMandatory: 'This lab keeps a photograph of every finished stage.',
    add: 'Add a photograph',
    retake: 'Take another',
    skip: 'Finish without one',
    confirm: 'Attach and finish',
    uploading: 'Sending the photograph',
    failed: 'The photograph could not be sent, so the case has not moved on.',
    cancel: 'Cancel',
    inputLabel: 'Proof of work photograph',
    // The sheet, request of 4 October 2026, section 6b.
    take: 'Take a photograph',
    another: 'Add another',
    retakeInputLabel: 'Retake proof of work photograph',
    photo: (n: number) => `Photograph ${n}`,
    remove: (n: number) => `Remove photograph ${n}`,
    retakeOne: (n: number) => `Retake photograph ${n}`,
    ready: (n: number) => (n === 1 ? '1 photograph ready' : `${n} photographs ready`),
    sent: 'sent',
    limit: (n: number) => `Up to ${n} photographs.`,
    note: 'Note for Lab Notes (optional)',
    notePlaceholder: 'What the photographs show, if it needs saying',
    unsaved: 'Discard the photographs and note you have not sent?',
    keep: 'Keep editing',
    discard: 'Discard',
    close: 'Close proof of work',
    cardTitle: 'Proof of work',
    takenBy: 'Taken by',
    none: 'No photographs of finished work on this case.',
    marker: 'Photograph of the finished work',
    settingLabel: 'Proof of work',
    settingHint: 'Whether the finished work is photographed at Mark done.',
    settingOff: 'Off',
    settingOffDetail: 'Nobody is asked for a photograph.',
    settingOptional: 'Optional',
    settingOptionalDetail: 'A photograph is offered at Mark done and may be skipped.',
    settingMandatory: 'Mandatory',
    settingMandatoryDetail: 'A stage cannot be completed without a photograph of the finished work.',
    settingSaved: 'Proof of work is now',
    required: WORK_PROOF_REFUSAL.required,
    switchedOff: WORK_PROOF_REFUSAL.switchedOff,
    startFirst: WORK_PROOF_REFUSAL.startFirst,
  },

  /*
   * The image viewer: every photograph in the app opens in it, over the screen
   * it was on. The request of 5 October 2026 (third), section 6.
   */
  viewer: {
    title: 'Photographs',
    close: 'Close the photographs',
    previous: 'Previous photograph',
    next: 'Next photograph',
    // "3 / 8", as the request writes it.
    counter: (n: number, total: number) => `${n} / ${total}`,
    // The same in words: the name of a photograph without a caption, and of its tile in the strip.
    position: (n: number, total: number) => `Photograph ${n} of ${total}`,
    thumbnails: 'All photographs',
    takenBy: (name: string) => `Uploaded by ${name}`,
    // A photograph still on the device: an intake queue, a proof not yet attached.
    notSentYet: 'Not sent yet',
    // The tile that opens the viewer, named by what it shows.
    open: (label: string) => `Open ${label}`,
    loading: 'Loading the photograph',
    failed: 'This photograph could not be shown.',
    download: 'Download',
    downloading: 'Downloading',
    downloadFailed: 'The photograph could not be downloaded.',
    // On the toast after the share sheet was refused: a second try, from a tap.
    save: 'Save',
  },

  routeChoice: {
    title: 'Where does the case go next?',
    lead: 'This case is flagged, so it does not follow the normal route. Pick the department that takes it now. The flag clears when it passes Final Check.',
    confirm: 'Complete and send',
    // Manual routing, set on the Settings screen. The usual next station is preselected.
    manualLead: 'Pick the station that takes this case next. The usual next station is already selected.',
    // Only the person: the department is already decided. Section 5.2 of the
    // request of 20 September 2026.
    personLead: 'It goes to the next tray on its route. Hand it to someone there, or leave it on the tray.',
    // Request of 4 October 2026, sections 5 and 6.
    usual: 'next on the route',
    searchPeople: 'Search anyone by name',
    noMatch: 'Nobody by that name.',
    note: 'Note for Lab Notes (optional)',
    notePlaceholder: 'Anything the next tray should know?',
  },

  scan: {
    title: 'Scan a tray',
    lead: 'Point the camera at the code on the tray card.',
    scanning: 'Scanning',
    torchOn: 'Torch on',
    torchOff: 'Torch off',
    unknown: 'This code is not in the system',
    denied: 'The camera is not available',
    deniedDetail: 'Type the kit or QR code instead. A broken camera must not stop production.',
    manualLabel: 'Job order, kit or tray code',
    manualPlaceholder: 'e.g. JO-2047',
    manualSubmit: 'Open',
    manualToggle: 'Enter job order number manually',
    manualTitle: 'Enter the job order number',
    manualLead: 'The number is printed large at the top of the tray sheet.',
    cancel: 'Cancel',
    // Request of 4 October 2026 (second), section 5.
    opening: (code: string) => `Opening ${code}`,
    fromPhoto: 'Scan from photo',
    photoLabel: 'Photograph of a tray code',
    readingPhoto: 'Reading the photo',
    noCodeInPhoto: 'No QR code found in that photo. Try again closer, with the code flat and in focus.',
    zoom: 'Zoom',
    backToCamera: 'Use the camera',
    tryAgain: 'Try the camera again',
    deniedTitle: 'Camera access is off for Ceramica',
    deniedLead: 'Scanning needs the camera. You can still type the job order number or scan from a photo.',
    stepsIphone: [
      'Open the Settings app, scroll down and tap Safari.',
      'Tap Camera and choose Allow (or Ask).',
      'If Ceramica is on your Home Screen and still cannot use the camera, close it fully and open it again.',
    ],
    stepsIphoneInstalled: [
      'Open the Settings app, scroll down and tap Safari, then Camera, and choose Allow or Ask.',
      'Close Ceramica fully (swipe it away) and open it again from the Home Screen.',
      'If it still does not ask, remove Ceramica from the Home Screen and add it again from Safari.',
    ],
    stepsAndroid: [
      'Tap the lock or settings icon beside the address, then Permissions, and turn Camera on.',
      'In the installed app: press and hold the Ceramica icon, tap App info, Permissions, Camera, Allow.',
    ],
    stepsOther: ['Allow the camera for this site in the browser settings, then try again.'],
  },

  odontogram: {
    chartLabel: 'Teeth selected',
    emptyLabel: 'Tooth chart, nothing selected',
    toothLabel: 'Tooth',
    none: 'No teeth selected yet',
    oneSelected: '1 tooth selected',
    manySelected: 'teeth selected',
    // The type names above and below the arches.
    molar: 'Molar',
    premolar: 'Premolar',
    canine: 'Canine',
    incisors: 'Incisors',
    // The legend under the chart, and the word between a span's two ends.
    bridge: 'Bridge',
    bar: 'Titanium bar',
    to: 'to',
    help: 'Tap a tooth to mark it on the case. A bridge or bar is drawn above the teeth it covers.',
  },

  intake: {
    label: 'Intake',
    title: 'New job order',
    hintCadcam:
      'Create the digital job order from the email case. You may set the start date once; only Management can change dates afterward.',
    hintOther: 'Create the digital job order from the email case. Reception and management intake.',
    dentist: 'Referring dentist',
    dentistPlaceholder: 'Dr. name',
    patient: 'Patient',
    patientPlaceholder: 'Patient name',
    /*
     * The doctor and the patient, looked up as they are typed. The request of
     * 6 October 2026 (second), items 4 and 5: the doctor field lists the
     * doctors that match. A name on neither list is kept, and the line under
     * the field says the case will add it. The lists are named for a screen
     * reader, and each Change button says what it changes.
     *
     * Since the request of 6 October 2026 (third), item 3, a patient is one
     * record whichever doctors it has been to, so the patient field searches
     * every patient. With a doctor chosen and nothing typed yet it opens on
     * that doctor's patients, which is what the third name is for.
     */
    doctorsList: 'Doctors that match',
    patientsList: 'Patients that match',
    patientsOfDoctorList: 'Patients of this doctor',
    doctorNew: 'New doctor: added when the case is saved',
    patientNew: 'New patient: added when the case is saved',
    // Said instead while names are listed under the field: the person may still
    // be on the way to one of them, and "new doctor" under a list of doctors
    // read as though the list did not count.
    doctorNewOrListed: 'Not one of these? A new doctor is added when the case is saved',
    patientNewOrListed: 'Not one of these? A new patient is added when the case is saved',
    // A name typed in full that is a record already: said with the clinic, which
    // is what tells one doctor from another of the same name.
    doctorKnown: (clinic: string | null) =>
      clinic === null ? 'Already in the system' : `Already in the system · ${clinic}`,
    /*
     * A patient's name typed in full that is one record already. The request
     * of 6 October 2026 (third), items 2 and 3. Said with the ID. When that
     * patient has been to the doctor on the case the line says no more than
     * that; when not, it says whose record the case joins, so two people of
     * one name are never made one by accident. Under either, the button makes
     * a record of their own for a different person, and `Use ... instead` is
     * the way back from that.
     */
    patientKnown: (code: string) => `Already in the system · ${code}`,
    patientSameName: (code: string, doctors: string) =>
      doctors === ''
        ? `Same name as ${code}. This case is added to that patient.`
        : `Same name as ${code}, seen by ${doctors}. This case is added to that patient.`,
    patientDifferent: 'A different person? Add as a new patient',
    patientUseExisting: (code: string) => `Use ${code} instead`,
    patientSeveral: 'More than one patient has this name. Choose one from the list.',
    // The doctors a patient has been to, as a row has room for them: the first
    // names, then how many are left unnamed.
    doctorsInBrief: (names: readonly string[], more: number) =>
      `${names.join(', ')}${more > 0 ? ` +${String(more)} more` : ''}`,
    patientNoDoctors: 'No cases yet',
    doctorSeveral: 'More than one doctor has this name. Choose one from the list.',
    moreMatches: (count: number) =>
      `${String(count)} more. Keep typing to narrow the list.`,
    lookupFailed: 'The list could not be read. The name is kept as typed.',
    change: 'Change',
    changeDoctor: 'Change doctor',
    changePatient: 'Change patient',
    /** In place of the country. Section 1 of the request of 24 September 2026. */
    origin: 'Origin',
    inputType: 'Input type',
    inputDigital: 'Digital scan (CAD/CAM)',
    inputImpression: 'Physical impression (Plaster)',
    item: 'Item',
    itemHint: 'applies to teeth marked from now on',
    itemPerTooth: 'Item per tooth',
    itemPerToothHint: 'Each tooth can be a different item. Change any that differ.',
    teeth: 'Teeth (FDI) · tap the teeth on the chart',
    shade: 'Shade',
    shadePlaceholder: 'Choose or type a shade',
    units: 'Units',
    unitsBelowChart: 'The chart says more. The case counts',
    unitsBelowChartTail: 'until a tooth is unmarked.',
    unitsMarked: 'marked on the chart,',
    unitsUnnamed: 'tooth not identified yet',
    unitsUnnamedPlural: 'teeth not identified yet',
    unitsPlaceholder: 'e.g. 4',
    noUnits: 'Mark the teeth on the chart, or type how many this case covers',
    /*
     * The bench the case opens at, the last row before Create. Section 4 of the
     * request of 20 September 2026: a case used to be created at Intake, which
     * reception cannot work and CAD / CAM cannot see.
     */
    startStage: 'Send this case to',
    startDate: 'Start date',
    startLockCadcam: 'CAD/CAM sets once',
    startLockOther: 'set at intake',
    deliveryDate: 'Delivery date',
    deliveryLockEditable: 'management can override',
    deliveryLockAuto: 'auto, start + 7 working days',
    // The lab's paper job order, field for field.
    specification: 'Case specification',
    translucency: 'Translucency',
    anatomy: 'Anatomy',
    incisal: 'Incisal transparency',
    occlusal: 'Occlusal stain',
    ingot: 'Ingot press',
    ingotPlaceholder: 'e.g. IPS e.max Press LT',
    block: 'Zircon block',
    blockPlaceholder: 'e.g. Katana STML',
    stain: 'Stain',
    stainPlaceholder: 'e.g. Light',
    itemsIncluded: 'Items included',

    photos: 'Case photos',
    photosHint:
      'Photograph the impression and the mouth. Whoever scans the tray sees these on the job view.',
    photosAdd: 'Add photos',
    photosAddMore: 'Add more',
    photosRemove: 'Remove',
    photosInputLabel: 'Case photo files',
    photosFailed: 'photos could not be attached. The job order was still created.',
    instructions: 'Instructions',
    instructionsPlaceholder: 'Notes for the lab',
    clear: 'Clear',
    create: 'Create job order',
    creating: 'Creating',
    footnote:
      'Start date is set here by CAD/CAM. After creation, only Management can modify the start or delivery date. Delivery auto-calculates as start plus 7 working days.',
    noTeeth: 'Select the teeth on the chart first',
    // Bridges and titanium bars, under the item per tooth.
    bridges: 'Bridges & bars',
    bridgesHint:
      'Runs from one tooth to another. The teeth it covers get a line above them on the chart.',
    spanType: 'Type',
    spanName: 'Name',
    spanNamePlaceholder: 'e.g. Anterior bridge',
    spanFrom: 'From tooth',
    spanTo: 'To tooth',
    spanBridge: 'Bridge',
    spanBar: 'Titanium bar',
    addBridge: 'Add bridge',
    addBar: 'Add titanium bar',
    removeSpan: 'Remove',
    spanNameRequired: 'Give the bridge or bar a name',
    // The four rules, worded once in the domain and shared with the server.
    spanOneArch: SPAN_RULE.oneArch,
    spanTwoTeeth: SPAN_RULE.twoTeeth,
    spanUnmarked: SPAN_RULE.unmarked,
    spanSharedName: SPAN_RULE.sharedName,
    spanOverlapBridge: SPAN_RULE.overlap.bridge,
    spanOverlapBar: SPAN_RULE.overlap.titaniumBar,
    dentistRequired: 'Enter the referring dentist',
    patientRequired: 'Enter the patient name',
    created: 'created and entered Intake',
    printSheets: 'Print the job sheets',
  },

  print: {
    sheetTitle: 'Job order',
    case: 'Case',
    dentist: 'Referring dentist',
    patient: 'Patient',
    origin: 'Origin',
    received: 'Received',
    startDate: 'Start date',
    deliveryDate: 'Delivery date',
    urgent: 'URGENT',
    restoration: 'Restoration',
    item: 'Item',
    teethFdi: 'Teeth (FDI)',
    teeth: 'Teeth',
    shade: 'Shade',
    units: 'Units',
    specification: 'Specification',
    ingot: 'Ingot press',
    block: 'Zircon block',
    translucency: 'Translucency',
    anatomy: 'Anatomy',
    incisal: 'Incisal',
    occlusal: 'Occlusal stain',
    stain: 'Stain',
    input: 'Input',
    itemsReceived: 'Items received',
    bridges: 'Bridges & bars',
    instructions: 'Instructions',
    // Only for a text longer than one page can hold at the smallest readable size.
    continued: 'Continues in the system: open the case to read the rest.',
    // The lab's own, printed only when there is something in it. Section 9.4.
    redoOf: 'REDO OF',
    // "Returned:", section 7.5 of the request of 16 September 2026.
    redoReason: 'Returned',
    printAction: 'Print',
    close: 'Close',
    preparing: 'Preparing the sheet',
  },

  /*
   * The notes on a case, gathered by meaning. Section 3 of the request of
   * 16 September 2026: a note should say what it is, where it came from, who it
   * is for, who wrote it and when.
   */
  /*
   * The instructions at the top of a case: one field, labelled "Instructions"
   * everywhere. Section 5 of the request of 5 October 2026. There were two, the
   * doctor's and the lab's own (section 9 of the request of 20 September 2026);
   * a case that had both keeps both, merged under a heading each.
   */
  instructions: {
    region: 'Instructions',
    title: 'Instructions',
  },

  notes: {
    title: 'Notes on this case',
    kindNote: 'Note',
    kindDone: 'Marked done',
    kindAssign: 'Assigned',
    kindMove: 'Moved',
    kindResolved: 'Resolved',
    kindPassed: 'Passed Final Check',
    kindProof: 'Proof of work',
    kindRework: 'Sent back from Final Check',
    kindFlag: 'Flagged to another tray',
    kindHold: 'Case put on hold',
    kindDeletion: 'Deletion',
    kindRedo: 'Came back as a remake',
    // The two steps of a case after it leaves the lab. The request of
    // 5 October 2026 (third), section 11.
    kindShipped: 'Shipped',
    kindReceived: 'Received',
    from: 'from',
    to: 'to',
    none: 'No notes on this case yet.',
    bySystem: 'The lab',
    add: 'Add a note',
    adding: 'Saving',
    placeholder: 'What should the next tray know?',
    // The job view shows the newest few and offers the rest in words that say
    // how many. Request of 24 September 2026.
    showAll: 'Show all',
    showFewer: 'Show fewer',
    plural: 'notes',
  },

  /*
   * Lab Notes: the conversation on a case, apart from what the doctor asked for.
   * Request of 4 October 2026, section 7.
   */
  labNotes: {
    // An action that was taken back: the entry stays, marked. Section 6 of the
    // request of 5 October 2026.
    undone: 'Undone',
    undoneBy: (name: string) => `Undone by ${name}`,
    newMessages: 'New messages',
    title: 'Lab Notes',
    open: 'Lab Notes',
    openWithUnread: (count: number) => `Lab Notes, ${count} unread`,
    lead: (jobOrderNo: string) => `The lab's conversation on ${jobOrderNo}. The doctor does not see it.`,
    close: 'Close Lab Notes',
    back: 'Back to the case',
    empty: 'Nothing written on this case yet. Start the conversation below.',
    loading: 'Loading Lab Notes',
    write: 'Write a message',
    placeholder: 'Message the lab about this case',
    send: 'Send',
    sending: 'Sending',
    // Under a message once the server has it, until the next one is sent. The
    // request of 5 October 2026 (third), section 4.
    sent: 'Sent',
    failed: 'Not sent.',
    retry: 'Retry',
    discard: 'Discard',
    everyone: 'Everyone on the case',
    privateTo: (name: string) => `Only ${name}`,
    onlyVisibleTo: (name: string) => `Only visible to ${name}`,
    privateFromTo: (from: string, to: string) => `Private, from ${from} to ${to}`,
    whoSees: 'Who sees it',
    you: 'You',
    newBelow: 'New messages below',
  },

  freshness: {
    refresh: 'Refresh',
    updated: 'Updated',
    updating: 'Updating',
    offline: 'Offline, saved copy',
    pull: 'Pull to refresh',
    release: 'Release to refresh',
  },

  /*
   * The one confirmation before a case action, in plain words. Request of
   * 4 October 2026 (second), section 1.
   */
  confirm: {
    done: (jo: string, to: string | null, person: string | null) =>
      to === null
        ? `Mark ${jo} as done?`
        : `Mark ${jo} as done and send it to ${to}${person === null ? '' : `, for ${person}`}?`,
    flag: (jo: string, to: string | null, person: string | null) =>
      to === null
        ? `Flag ${jo}? Pick the department it goes to.`
        : `Flag ${jo} and send it to ${to}${person === null ? '' : `, for ${person}`}?`,
    rework: (jo: string, to: string | null, person: string | null = null) =>
      to === null
        ? `Send ${jo} back for rework? Pick the department.`
        : person === null
          ? `Send ${jo} back to ${to} for rework?`
          : `Send ${jo} back to ${to} for rework, for ${person}?`,
    assign: (jo: string, person: string | null) =>
      person === null ? `Leave ${jo} unassigned?` : `Assign ${jo} to ${person}?`,
    take: (jo: string, from: string, to: string) =>
      `Take ${jo} from ${from}, move it to ${to} and start your clock?`,
    start: (jo: string, at: string) => `Start ${jo} at ${at} on your clock?`,
    // Mark received, on the shipping screen, an archive row and the case record.
    // The request of 5 October 2026 (third), section 11.
    received: (jo: string) => `Mark ${jo} as received by the customer?`,
    /*
     * Cases outside the bench: intake, the order drawer, editing a case, the
     * archive and its deleted set, shipping and a selection of cases. The
     * request of 5 October 2026 (third), section 2. Each is a plain question
     * that names the case, or says how many, and adds what follows only where
     * that cannot be seen from the button. Releasing a hold and marking a tray
     * urgent are asked with `releaseHold` and `urgent` below, the sentences the
     * other screens use.
     */
    /*
     * A remake names its original, and one from before this system says so in
     * the words every screen uses for it (`redo.beforeSystem`): the request of
     * 6 October 2026 (second), item 1. The return type is written out because
     * the sentence reads another group of this object.
     */
    createCase: (
      doctor: string,
      units: number,
      startsAt: string,
      redoOf: string | null,
      redoOfLegacy = false,
    ): string => {
      const what =
        redoOf === null
          ? 'a job order'
          : `a redo of ${redoOf}${redoOfLegacy ? ` ${strings.redo.beforeSystem}` : ''}`;
      return `Create ${what} for ${doctor}, ${
        units === 1 ? '1 unit' : `${String(units)} units`
      }, starting at ${startsAt}?`;
    },
    saveCase: (jo: string) => `Save the changes to ${jo}?`,
    /**
     * Save, when it changes the input type or an item: the lab's request of
     * 18 October 2026. What changes, where the case goes from here when its
     * route changes with it, and that a new item takes today's price.
     */
    saveItems: (
      jo: string,
      change: { item: string | null; input: string | null; teeth: number },
      route: RouteAfterEdit | null,
    ) => {
      const parts = [
        change.item === null ? null : `the item becomes ${change.item}`,
        change.input === null ? null : `the input type becomes ${change.input}`,
        change.teeth === 0
          ? null
          : change.teeth === 1
            ? '1 tooth takes another item'
            : `${String(change.teeth)} teeth take another item`,
      ].filter((part): part is string => part !== null);
      const last = parts.pop();
      const said = last === undefined ? '' : parts.length === 0 ? last : `${parts.join(', ')} and ${last}`;
      const what = said === '' ? '' : ` ${said.charAt(0).toUpperCase()}${said.slice(1)}.`;
      const where =
        route === null
          ? ''
          : route.kind === 'next'
            ? ` It is at ${STAGE_LABEL[route.at]} and goes on from there to ${STAGE_LABEL[route.next]}.`
            : route.kind === 'off'
              ? ` It is at ${STAGE_LABEL[route.at]}, which its new route does not pass, so whoever finishes it there chooses where it goes next.`
              : ' Its kits stay where they are and each goes on by the new route.';
      const price = change.item !== null || change.teeth > 0 ? " A new item takes today's price." : '';
      return `Save the changes to ${jo}?${what}${where}${price}`;
    },
    saveDates: (jo: string, start: string, delivery: string) =>
      `Change the dates of ${jo} to start ${start} and delivery ${delivery}?`,
    removeCasePhoto: (jo: string | null) =>
      `Remove this photograph${jo === null ? '' : ` from ${jo}`}? It cannot be brought back.`,
    // The line under these two, and under the three for a selection, says what follows.
    deleteCase: (jo: string) => `Delete ${jo}?`,
    askDeletion: (jo: string) => `Ask for ${jo} to be deleted?`,
    approveDeletion: (jo: string) => `Approve the deletion of ${jo}? It leaves every list now.`,
    refuseDeletion: (jo: string) => `Refuse the deletion of ${jo}? The case carries on as before.`,
    withdrawDeletion: (jo: string) => `Withdraw the request to delete ${jo}?`,
    restoreCase: (jo: string) => `Restore ${jo}? It goes back to where it was.`,
    // The line under it says that nothing brings the case back.
    purgeCase: (jo: string) => `Delete ${jo} for good?`,
    unarchive: (jo: string) => `Take ${jo} out of the archive?`,
    unarchiveMany: (count: number) => `Take ${String(count)} cases out of the archive?`,
    archiveMany: (count: number) => `Archive ${count === 1 ? '1 case' : `${String(count)} cases`}?`,
    deleteMany: (count: number) => `Delete ${count === 1 ? '1 case' : `${String(count)} cases`}?`,
    askDeleteMany: (count: number) =>
      `Ask for ${count === 1 ? '1 case' : `${String(count)} cases`} to be deleted?`,
    assignMany: (count: number, to: string | null, person: string | null) => {
      const cases = count === 1 ? '1 case' : `${String(count)} cases`;
      if (to !== null) return `Send ${cases} to ${to}${person === null ? '' : `, for ${person}`}?`;
      return person === null
        ? `Assign ${cases}? Choose a department, a person, or both.`
        : `Assign ${cases} to ${person}?`;
    },
    // Null while Other is chosen and the courier has not been named yet.
    dispatch: (jo: string, courier: string | null) =>
      `Dispatch ${jo} by ${courier ?? 'courier'}?`,
    dispatchByLab: (jo: string) => `Dispatch ${jo} with the lab's own delivery?`,
    saveTracking: (jo: string, code: string) =>
      code === '' ? `Save the waybill code of ${jo}?` : `Save waybill code ${code} for ${jo}?`,
    saveShipping: (jo: string) => `Save the changes to the shipping of ${jo}?`,
    /**
     * How a case left, corrected: the lab's request of 22 October 2026. The
     * courier's name when it went by courier, null when the lab drove it.
     */
    saveShippingMethod: (jo: string, courier: string | null) =>
      courier === null
        ? `Save the changes to the shipping of ${jo}? It is recorded as the lab's own delivery from now on, and its courier and waybill code are taken off.`
        : `Save the changes to the shipping of ${jo}? It is recorded as going with ${courier} from now on, and its driver is taken off.`,
    /*
     * Money and the price list: Payments, the receipt and Items. The request
     * of 5 October 2026 (third), section 2. A payment has no number of its own,
     * so the job order number names it. Releasing a hold is asked the same way
     * on Payments, On Hold and Shipping, so the three screens share one
     * sentence. Mark as reviewed and Mark paid keep the sentences they already
     * had, in `payments`.
     */
    releaseHold: (jo: string) => `Release the hold on ${jo}?`,
    /*
     * Releasing a case to be shipped before it is paid, one or many, marking
     * many paid, and a doctor's own prices. The lab's request of
     * 17 October 2026, items 8 to 11.
     */
    releaseCase: (jo: string) =>
      `Release ${jo} to be shipped before it is paid? It stays on To be paid until it is paid.`,
    takeBackRelease: (jo: string) =>
      `Take back the release of ${jo}? It cannot be dispatched until it is paid or released again.`,
    payMany: (count: number, total: string) =>
      count === 1 ? `Mark 1 case paid? It comes to ${total}.` : `Mark ${String(count)} cases paid? They come to ${total}.`,
    releaseMany: (count: number) =>
      count === 1
        ? 'Release 1 case to be shipped before it is paid?'
        : `Release ${String(count)} cases to be shipped before they are paid?`,
    takeBackMany: (count: number) =>
      count === 1 ? 'Take back the release of 1 case?' : `Take back the release of ${String(count)} cases?`,
    setDoctorPrice: (item: string, doctor: string, price: string) =>
      `Charge ${item} at ${price} for ${doctor}? Only their next cases take it; cases already taken in keep their prices.`,
    clearDoctorPrice: (item: string, doctor: string, price: string) =>
      `Put ${item} back to the list price of ${price} for ${doctor}? Only their next cases take it.`,
    hold: (jo: string, reason: string) =>
      `Put ${jo} on hold? Reason: ${reason}. Any clock running on it stops.`,
    savePrices: (jo: string, count: number, total: string) =>
      `Save ${count === 1 ? '1 changed price' : `${String(count)} changed prices`} for ${jo}? The total becomes ${total}. The price list stays as it is.`,
    // The category and the kind are said because neither can be changed afterwards.
    addItem: (name: string, category: string, newCategory: boolean, price: string, perSpan: boolean) =>
      `Add ${name} to ${newCategory ? `the new category ${category}` : category} at ${price}, charged per ${perSpan ? 'span' : 'unit'}?`,
    renameItem: (from: string, to: string) =>
      `Rename ${from} to ${to}? The name changes on every case that has it.`,
    repriceItem: (name: string, from: string | null, to: string) =>
      from === null
        ? `Set the price of ${name} to ${to}? New cases only.`
        : `Change the price of ${name} from ${from} to ${to}? New cases only.`,
    renameAndRepriceItem: (from: string, to: string, priceFrom: string | null, priceTo: string) =>
      priceFrom === null
        ? `Rename ${from} to ${to} and set its price to ${priceTo}? The name changes on every case that has it. The price is for new cases only.`
        : `Rename ${from} to ${to} and change its price from ${priceFrom} to ${priceTo}? The name changes on every case that has it. The price is for new cases only.`,
    retireItem: (name: string) => `Retire ${name}? Intake stops offering it.`,
    restoreItem: (name: string) => `Put ${name} back on the price list? Intake offers it again.`,
    /*
     * Admin and the floor: Users, the access matrix, Settings, a profile, the
     * case screen, Final Check, the Overview and the not-synced tray. The
     * request of 5 October 2026 (third), section 2. Each names the person, the
     * case or the setting, and says what follows where that is not obvious.
     */
    // The money of a case. The request of 8 October 2026 (fourth).
    takePayment: (jo: string, amount: string, left: string | null) =>
      left === null
        ? `Record ${amount} for ${jo}? That pays it in full.`
        : `Record ${amount} for ${jo}? ${left} will still be owed.`,
    useCredit: (jo: string, amount: string, left: string | null) =>
      left === null
        ? `Use ${amount} of the doctor's credit on ${jo}? That pays it in full.`
        : `Use ${amount} of the doctor's credit on ${jo}? ${left} will still be owed.`,
    savePayment: (jo: string) => `Save the changes to this payment on ${jo}?`,
    removePayment: (jo: string, amount: string, toCredit: boolean) =>
      toCredit
        ? `Remove the payment of ${amount} from ${jo}? It goes back to the doctor's credit.`
        : `Remove the payment of ${amount} from ${jo}?`,
    addAdjustment: (jo: string, what: string, from: string, to: string) =>
      `Add ${what} to ${jo}? Its total due goes from ${from} to ${to}.`,
    removeAdjustment: (jo: string, what: string) => `Remove ${what} from the bill of ${jo}?`,
    addCredit: (name: string, amount: string) => `Add ${amount} to the credit of ${name}?`,
    removeCredit: (name: string, amount: string) => `Take ${amount} off the credit of ${name}?`,
    markPaidNothingOwed: (jo: string) => `Mark ${jo} as paid? It owes nothing.`,
    markUnpaid: (jo: string) => `Mark ${jo} as unpaid?`,
    restoreUser: (name: string) => `Restore access for ${name}?`,
    createUser: (name: string, role: string) => `Create ${name} as ${role}?`,
    // What changed, by the names the form gives its fields.
    editUser: (name: string, fields: readonly string[]) =>
      `Save changes to ${name}: ${fields.join(', ')}?`,
    setPassword: (name: string) =>
      `Set a new password for ${name}? They are signed out on every device.`,
    ownPassword: 'Change your password? Your other devices are signed out.',
    grantAccess: (role: string, label: string, on: boolean) =>
      on ? `Give ${role} "${label}"?` : `Take "${label}" away from ${role}?`,
    userAccess: (name: string) => `Save these access exceptions for ${name}?`,
    routing: (mode: string) => `Change production routing to "${mode}"?`,
    workProof: (mode: string) => `Change proof of work to "${mode}"?`,
    takeOverAtFinalCheck: (allowed: boolean) =>
      allowed
        ? 'Allow a case at Final Check to be taken over?'
        : 'Stop cases at Final Check from being taken over?',
    dayEnd: (time: string | null) =>
      time === null ? 'Turn off the end-of-day stop?' : `Stop clocks at ${time} at the end of the day?`,
    afterHours: (time: string | null) =>
      time === null
        ? 'Turn off the after-hours stop? Those clocks then run to the next end of the day.'
        : `Stop clocks started after the end of the day at ${time}?`,
    geofence: (enabled: boolean) =>
      enabled
        ? 'Save the sign-in location and refuse station sign-in from outside it?'
        : 'Save the sign-in location without refusing anybody?',
    appColors: (theme: string, background: string) =>
      `Save the app colours ${theme} and ${background}?`,
    resetAppColors: 'Go back to the default app colours?',
    appIcons: (file: string) => `Replace the app icon with ${file}? Installed apps pick it up later.`,
    resetAppIcons: 'Go back to the built-in app icon?',
    removeProfilePhoto: (name: string | null) =>
      name === null ? 'Remove your profile photo?' : `Remove the profile photo of ${name}?`,
    urgent: (jo: string, on: boolean) => (on ? `Mark ${jo} as urgent?` : `Remove urgent from ${jo}?`),
    specField: (jo: string, label: string, value: string) =>
      value === '' ? `Clear ${label} on ${jo}?` : `Set ${label} on ${jo} to ${value}?`,
    passFinalCheck: (jo: string) => `Pass ${jo} at Final Check and move it to Ready?`,
    stopClock: (name: string, jo: string) => `Stop the clock of ${name} on ${jo}?`,
    discardQueued: (what: string, kit: string) =>
      `Discard "${what}" for ${kit}? It will not be sent.`,
    /*
     * What a confirmation is headed and answered with when the action brings no
     * words of its own. The request of 5 October 2026 (third), section 2: an
     * action passes its own verb as the answer wherever it has one.
     */
    title: 'Confirm',
    yes: 'Yes',
    cancel: 'Cancel',
    started: 'started on your clock',
  },

  /* A person's profile. Request of 4 October 2026 (second), section 7. */
  profile: {
    haptics: 'Vibrate on confirmations',
    hapticsHint: 'A light tap when a scan is read, a case is marked done or flagged.',
    title: 'Profile',
    open: 'Your profile',
    openFor: (name: string) => `Profile of ${name}`,
    loading: 'Loading the profile',
    supervisor: 'Department supervisor',
    intake: 'Intake cover',
    disabled: 'Access revoked',
    changePhoto: 'Change photo',
    addPhoto: 'Add a photo',
    removePhoto: 'Remove photo',
    photoLabel: 'Profile photo',
    photoSaved: 'Photo saved',
    photoRemoved: 'Photo removed',
    contact: 'Contact',
    phone: 'Phone',
    email: 'Email',
    notSet: 'Not set',
    work: 'Work',
    periodLabel: 'Period',
    // All time first and by default. The lab's request of 17 October 2026, item 4.
    allTime: 'All time',
    today: 'Today',
    week: 'Last 7 days',
    month: 'Last 30 days',
    casesWorked: 'Cases worked on',
    teethWorked: 'Teeth / units',
    jobOrdersCreated: 'Job orders created',
    tasks: (n: number) => (n === 1 ? '1 task completed' : `${n} tasks completed`),
    current: 'Current task',
    nothingCurrent: 'Nothing on the clock right now.',
    running: 'Running',
    paused: 'Paused',
    recent: 'Recent activity',
    nothingRecent: 'Nothing yet.',
    account: 'Account',
    changePassword: 'Change password',
    signOut: 'Sign out',
    version: 'App version',
    memberSince: 'Member since',
    lastSignIn: 'Last signed in',
    editInUsers: 'Edit in Users',
  },

  /*
   * Versions and what changed in each. The request of 5 October 2026 (third),
   * section 8. The labels only: the sentences of the list itself are content
   * and live with the numbers, in src/shared/release/changelog.ts. Not named
   * `release`, which in this product is what is done to a hold.
   */
  versions: {
    open: "What's new",
    title: "What's new",
    thisDevice: (label: string) => `This device runs ${label}`,
    build: (id: string) => `Build ${id}`,
    current: 'This version',
    // Written out rather than asked of the browser, whose short September is "Sept".
    months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  },

  /* The scanned case, read on a phone. Request of 4 October 2026 (second), section 8. */
  caseView: {
    status: 'Status',
    department: 'Department',
    assignedTo: 'Assigned to',
    unassigned: 'Unassigned',
    due: 'Due',
    onHold: 'On hold',
    archived: 'Archived',
    rework: 'Rework',
    inProgress: 'In progress',
    waiting: 'Waiting',
    details: 'Case details',
    doctor: 'Doctor / clinic',
    patient: 'Patient',
    caseType: 'Case type',
    material: 'Material',
    shade: 'Shade',
    units: 'Units',
    received: 'Received',
    enlarge: 'Enlarge',
    enlargeLabel: 'Enlarge the teeth chart',
    chartTitle: 'Teeth chart',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    // A quarter turn, so the chart fills a phone held upright. The request of 5 October 2026 (third), section 5.
    rotate: 'Rotate the chart',
    resetZoom: 'Fit to screen',
    legend: 'Legend',
    legendSelected: 'Tooth on this case',
    legendBridge: 'Bridge',
    legendBar: 'Titanium bar',
    pinchHint: 'Pinch or use the buttons to zoom; drag to move.',
    // The cross at the top right of the enlarged chart.
    closeChart: 'Close the teeth chart',
    scanToTake: 'Scan to take',
  },

  // A notification about a case that has moved on. Request of 5 October 2026.
  movedOn: {
    title: 'No longer with you',
    statement: (jo: string, stage: string) =>
      `${jo} is no longer with you; it is at ${stage} now. View the case?`,
    view: 'Yes, view it',
    no: 'No',
    readOnly: 'View only: this case is no longer with you.',
  },
  // Pause, asked first. Request of 5 October 2026.
  pause: {
    title: 'Pause',
    statement: (jo: string, at: string) =>
      `Pause ${jo} at ${at}? The time is kept; Resume or a scan of the tray takes it up again.`,
    confirm: 'Pause',
    cancel: 'Keep working',
  },
  sheet: {
    close: 'Close',
    // The one button of a notice. The request of 5 October 2026 (third), section 1.
    ok: 'OK',
    unsaved: 'Discard the note you have written?',
    keep: 'Keep editing',
    discard: 'Discard',
  },

  table: {
    // The order control in the filter bar, section 3 of the request of
    // 13 September 2026. `sortBy` below is the phone's fallback for the column
    // headers, which is a different control ordering by a different thing.
    order: 'Order',
    byColumn: 'By column',
    dueSoonest: 'Due soonest',
    urgentFirst: 'Urgent first',
    newestFirst: 'Newest first',
    oldestFirst: 'Oldest first',
    longestWaiting: 'Longest waiting',
    doctorAtoZ: 'Doctor A to Z',
    // The mark beside the job order number of a remake. The request of 6 October 2026 (second), item 2.
    redo: 'Redo case',
    // The mark beside the job order number of a case on hold. The request of 20 October 2026.
    onHold: 'On hold',
    // The archive's two orders by the hand-over. The request of 5 October 2026
    // (third), section 11: the day a case left is its Shipped day, and the cases
    // still out lead the second, the one out longest at the top.
    recentlyShipped: 'Recently shipped',
    longestInShipped: 'Longest in Shipped',
    waitingFirst: 'Waiting first',
    recentlyWorked: 'Most recently worked',
    // One origin's cases first. Section 2 of the request of 24 September 2026.
    localFirst: 'Local first',
    internationalFirst: 'International first',
    onlineFirst: 'Online first',
    origin: 'Origin',
    /*
     * The page control under every long table. Section 10.4 of the request of
     * 16 September 2026.
     */
    showing: 'Showing',
    to: 'to',
    ofTotal: 'of',
    perPage: 'Per page',
    previous: 'Previous',
    next: 'Next',
    page: 'page',
    of: 'of',
    pagesLabel: 'Pages',
    /** Under a figure counted from the rows on screen rather than the whole list. */
    onThisPage: 'on this page',
    sortBy: 'Sort by',
    sortDefault: 'Default order',
    ascending: 'ascending',
    descending: 'descending',
    empty: 'Nothing to show here.',
    // The three columns every case table leads with, and the one it ends on.
    jobOrder: 'Job order',
    doctor: 'Doctor',
    patient: 'Patient',
    status: 'Status',
    hidden: 'Hidden',
    urgent: 'Urgent',
    noMatch: 'Nothing matches these filters',
    noMatchDetail: 'Clear the search or a filter and look again.',
    /*
     * Customize columns, over every table. The request of 7 October 2026,
     * section 3: which columns, in which order, and the rows a page, kept on
     * the reader's account.
     */
    customize: {
      open: 'Customize columns',
      title: 'Customize columns',
      lead: 'Tick the columns this table shows, and drag them into the order you read them in. A column without a tick is one more it can show.',
      listLabel: 'The columns of this table, in your order',
      alwaysShown: 'Always shown',
      drag: (column: string) => `Drag ${column} to another place`,
      moveUp: (column: string) => `Move ${column} up`,
      moveDown: (column: string) => `Move ${column} down`,
      rowsPerPage: 'Rows per page',
      reset: 'Reset to default',
      done: 'Done',
      // The cross in the head of the sheet, which is not the Done under it.
      close: 'Close',
      saved: 'Saved to your account as you go, and the same on every device.',
    },
    /*
     * The columns a table offers and leaves out until its reader ticks them in
     * Customize columns: what its rows already carry. The request of
     * 8 October 2026 (second). One word each, whichever table offers it.
     */
    columns: {
      caseType: 'Case type',
      item: 'Item',
      value: 'Value',
      paid: 'Paid',
      yes: 'Yes',
      clinic: 'Clinic',
      redoOf: 'Redo of',
      // The number on the doctor's paper. The lab's request of 17 October 2026, item 7.
      serialNumber: 'Serial no.',
      carrier: 'Carrier',
      tracking: 'Tracking',
      units: 'Units',
      shade: 'Shade',
      due: 'Due',
      startDate: 'Start date',
      received: 'Received',
      notes: 'Notes',
      kit: 'Kit',
      assignedBy: 'Assigned by',
      // The lab's request of 11 October 2026: more of what a case carries, to switch on.
      inLab: 'In the lab',
      teeth: 'Teeth',
      photos: 'Photos',
      instructions: 'Instructions',
      cameWith: 'Came with',
      completed: 'Completed',
      sentBackAs: 'Sent back as',
      reason: 'Reason',
      requestedBy: 'Requested by',
      email: 'Email',
      website: 'Website',
      city: 'City',
      country: 'Country',
      lastCase: 'Last case',
      added: 'Added',
      doctors: 'Doctors',
    },
  },

  // The filter bar above every case table.
  filters: {
    // The one button a phone shows in place of the bar. The lab's request of 17 October 2026, item 2.
    searchAndFilters: 'Search and filters',
    inUse: (count: number) => `${String(count)} in use`,
    allTime: 'All time',
    today: 'Today',
    thisWeek: 'This week',
    thisMonth: 'This month',
    customRange: 'Custom range',
    from: 'From',
    to: 'To',
  },

  orders: {
    // Still being made: neither Ready nor Logistics. The lab's request of 17 October 2026, item 3.
    activeCases: 'Active cases',
    openCase: 'Open case',
    fullRecord: 'Full record',
    editCase: 'Edit case',
    editDates: 'Edit dates',
    startDate: 'Start date',
    deliveryDate: 'Delivery date',
    saveDates: 'Save dates',
    datesSaved: 'dates changed',
    markUrgent: 'Mark urgent',
    clearUrgent: 'Clear urgent',
    printSheet: 'Print the tray sheet',
    withWhom: 'With',
    nobodyYet: 'Unassigned',
    dispatchedAway: 'Dispatched',
    search: 'Search this list',
    searchPlaceholder: 'Job order, doctor, patient or origin',
    filtersLabel: 'Filter the orders',
    stage: 'Stage',
    allStages: 'All stages',
    allStatuses: 'All statuses',
    urgentOnly: 'Urgent only',
    // The Due filter, asked of the server. The request of 7 October 2026, sections 6 and 7.
    anyDue: 'Any due date',
    label: 'Cases',
    title: 'Orders',
    hint: 'Every case still in the lab. A dispatched order leaves this list and moves to the Archive.',
    caption: 'Job orders in the lab',
    due: 'Due',
    status: 'Status',
    emptyHeading: 'No orders in the lab',
    emptyDetail: 'Every order has been dispatched. They are in the Archive.',
    inProduction: 'In production',
    overdue: 'Overdue',
    urgent: 'Urgent',
    hidden: 'Hidden',
    drawerKits: 'Kits on this order',
    close: 'Close',
    kit: 'kit',
    kitsPlural: 'kits',
    // The line under an opened case's number: how its date stands. The lab's answer of 8 October 2026.
    countdownOverdue: (days: number) => `overdue by ${String(days)} ${days === 1 ? 'day' : 'days'}`,
    countdownToday: 'due today',
    countdownIn: (days: number) => `due in ${String(days)} ${days === 1 ? 'day' : 'days'}`,
    /** The countdown under the delivery date in the Due column: "5d". */
    daysShort: 'd',
    teeth: 'teeth',
    shade: 'shade',
  },

  /**
   * On Hold Cases. Section 5 of the request of 16 September 2026: a held case
   * was a group on Payments, a group on Shipping and a marked row on Orders,
   * and nobody had a list of them.
   */
  onHold: {
    label: 'Accounts',
    title: 'On Hold Cases',
    hint: 'Every case the lab has stopped, with the reason and how long it has been stopped. A case on hold cannot be given to anybody until the hold is lifted.',
    search: 'Search this list',
    searchPlaceholder: 'Job order, doctor, patient or reason',
    filtersLabel: 'Filter the cases on hold',
    reason: 'Why',
    since: 'On hold since',
    actions: 'Action',
    caption: 'Cases the lab has stopped',
    emptyHeading: 'Nothing is on hold',
    emptyDetail: 'Cases appear here when accounts or reception stop one.',
  },

  /*
   * The money of a case, of a doctor and of the lab. The request of
   * 8 October 2026 (fourth): a payment summary for every case, with its
   * discounts and charges and its payments one by one; a doctor's credit and
   * statement; and what the lab is owed, by doctor and by how long.
   */
  finance: {
    // The payment summary of one case.
    summaryLabel: 'Payment summary',
    summaryLoading: 'Reading the payment summary',
    backToPayments: 'Payments',
    notFound: 'That case is not in the system',
    notFoundDetail: 'It may have been deleted, or its number was mistyped.',
    doctor: 'Doctor',
    patient: 'Patient',
    // The day the case arrived: Received, everywhere. The lab's instruction of 11 October 2026.
    booked: 'Received',
    dueDate: 'Due',
    noDoctor: 'No doctor record',
    notCharged: 'A remake the lab does not charge for.',
    statementLink: 'Statement',
    totalDue: 'Total due',
    paid: 'Paid',
    balance: 'Balance',
    figuresLabel: 'What this case owes',
    paidOn: (day: string) => `Paid on ${day}`,
    // Marked paid, and edited since: the review of the change is where it is squared.
    owesSinceEdited: (amount: string) => `Marked paid, and ${amount} is owed since the case was edited.`,
    creditAvailable: (amount: string) => `The doctor has ${amount} of credit.`,
    actionsLabel: 'What can be done',
    addPayment: 'Add payment',
    useCredit: 'Use doctor credit',
    addAdjustment: 'Discount or charge',
    prices: 'Change prices',
    markPaid: 'Mark paid',
    markUnpaid: 'Mark unpaid',
    billHeading: 'Bill',
    billLines: 'The lines of the bill',
    teeth: 'Teeth',
    noTeeth: 'no teeth marked',
    perSpan: 'per span',
    was: 'was',
    linesTotal: 'Lines total',
    adjustmentsHeading: 'Discounts and charges',
    noAdjustments: 'No discount and no added charge.',
    adjustmentWhat: (kind: string, figure: string) => `${kind} ${figure}`,
    by: (name: string | null, when: string) => (name === null ? when : `${name} · ${when}`),
    remove: 'Remove',
    edit: 'Edit',
    removeAdjustmentLabel: (what: string) => `Remove ${what}`,
    paymentsHeading: 'Payments',
    paymentsList: 'The payments on this case',
    noPayments: 'Nothing has been paid yet.',
    reference: 'Reference',
    editPaymentLabel: (amount: string) => `Edit the payment of ${amount}`,
    removePaymentLabel: (amount: string) => `Remove the payment of ${amount}`,
    historyHeading: 'History',
    noHistory: 'Nothing has been done to this bill yet.',
    // Said after each thing done.
    paymentAdded: (amount: string) => `${amount} received`,
    creditUsed: (amount: string) => `${amount} of credit used`,
    paymentSaved: 'Payment corrected',
    paymentRemoved: 'Payment removed',
    adjustmentAdded: 'Bill changed',
    adjustmentRemoved: 'Removed from the bill',
    nowPaid: (jo: string) => `${jo} is paid in full`,

    // The form of one payment.
    paymentTitle: (jo: string) => `Payment · ${jo}`,
    creditTitle: (jo: string) => `Use doctor credit · ${jo}`,
    editPaymentTitle: (jo: string) => `Edit payment · ${jo}`,
    paymentLead: (balance: string) => `This case still owes ${balance}. Take all of it, or part.`,
    creditLead: (balance: string, credit: string) =>
      `This case still owes ${balance}, and the doctor has ${credit} of credit.`,
    editPaymentLead: 'Put right how it came, its reference, its day or its amount.',
    amount: 'Amount',
    method: 'How it was paid',
    methodNone: 'Not recorded',
    referencePlaceholder: 'Transfer or cheque number',
    paidOnLabel: 'Day it was paid',
    note: 'Note',
    noteOptional: 'Note (optional)',
    takePayment: 'Take payment',
    useCreditAction: 'Use credit',
    savePayment: 'Save',
    cancel: 'Cancel',
    discard: 'Leave without saving what you typed?',
    badAmount: 'Enter an amount above zero.',

    // The form of a discount or a charge.
    adjustmentTitle: (jo: string) => `Discount or charge · ${jo}`,
    adjustmentLead: 'For this case alone. The price list is not touched.',
    kind: 'Kind',
    asAmount: 'Amount',
    asPercent: 'Percent',
    figureMode: 'Figure',
    value: 'Figure',
    reason: 'Reason',
    reasonPlaceholder: 'e.g. returning customer, courier fee',
    preview: (from: string, to: string) => `Total due: ${from} becomes ${to}`,
    saveAdjustment: 'Add to the bill',

    // A doctor's statement, and the credit.
    statementLabel: 'Doctor statement',
    statementLoading: 'Reading the statement',
    doctorNotFound: 'That doctor is not in the system',
    doctorNotFoundDetail: 'The record may have been merged into another, or its ID was mistyped.',
    billed: 'Billed',
    outstanding: 'Outstanding',
    credit: 'Credit',
    statementFigures: 'This doctor in figures',
    openProfile: 'Doctor profile',
    casesHeading: 'Cases',
    casesCaption: (name: string) => `The cases of ${name}, as bills`,
    noCases: 'No cases yet',
    noCasesDetail: 'A case booked for this doctor appears here with what it owes.',
    // Under the four figures, when the doctor has archived cases: they are listed and not counted.
    archivedLeftOut: 'Archived cases are listed below and left out of these figures.',
    // Between the job order and patient the case table leads with and the status it ends on.
    colBooked: 'Received',
    colDue: 'Total due',
    colPaid: 'Paid',
    colBalance: 'Balance',
    colAge: 'Open for',
    days: (count: number) => `${String(count)}d`,
    archivedTag: 'Archived',
    creditHeading: 'Credit',
    creditBalance: 'Credit balance',
    creditList: "Every movement of this doctor's credit",
    noCredit: 'No credit has been added for this doctor.',
    addCredit: 'Add credit',
    removeCredit: 'Take off credit',
    addCreditTitle: (name: string) => `Add credit · ${name}`,
    removeCreditTitle: (name: string) => `Take off credit · ${name}`,
    addCreditLead: 'An advance, a refund or goodwill. It can then pay any of the doctor\'s cases, in whole or in part.',
    removeCreditLead: (balance: string) => `Paid back, or entered by mistake. The doctor has ${balance}.`,
    creditNote: 'What it is for',
    creditNotePlaceholder: 'e.g. advance for October',
    creditAdded: (amount: string) => `${amount} of credit added`,
    creditRemoved: (amount: string) => `${amount} of credit taken off`,
    onCase: (jo: string) => `on ${jo}`,

    // The overview, on Payments.
    overviewLabel: 'What the lab is owed',
    openCases: 'Unpaid cases',
    partPaid: 'Part paid',
    creditHeld: 'Credit held',
    agesLabel: 'Outstanding by how long the case has been open',
    agesHint: 'Counted from the day each case was received. Press an age to see its cases.',
    openAge: (age: string) => `Show the unpaid cases received ${age} ago`,
    casesOwing: (count: number) => `${String(count)} ${count === 1 ? 'case' : 'cases'}`,
    tabPart: 'Part paid',
    tabDoctors: 'By doctor',
    partEmptyHeading: 'No case is paid in part',
    partEmptyDetail: 'A case with some of its bill received and not all of it appears here.',
    doctorsCaption: 'What each doctor owes, and the credit each holds',
    doctorsEmptyHeading: 'Nothing is owed',
    doctorsEmptyDetail: 'Every case that owes something appears here under its doctor.',
    colDoctor: 'Doctor',
    colId: 'ID',
    colCases: 'Unpaid cases',
    colOldest: 'Oldest',
    colCredit: 'Credit',
    unlinked: 'Cases with no doctor record',
    summary: 'Summary',
    paidOfDue: (paid: string, due: string) => `${paid} of ${due} paid`,
    receiptSummary: 'Open the payment summary',
    receiptDue: 'Total due',
    receiptPaid: 'Paid so far',
    receiptBalance: 'Balance',
  },

  payments: {
    label: 'Accounts',
    title: 'Payments',
    hint: 'Cases stay Ready to Deliver until they are paid or released. Mark paid or Release lets them be dispatched. Put a case on hold to stop it leaving the lab.',
    onHold: 'On hold',
    /*
     * The bar and the table, which replaced four groups of cards. A search
     * across four groups is four searches, and a page across them is a page of
     * whichever group the cut falls in.
     */
    search: 'Search this list',
    searchPlaceholder: 'Job order, doctor, patient or origin',
    filtersLabel: 'Filter the payments',
    unpaidOnly: 'Unpaid only',
    value: 'Value',
    actions: 'Action',
    caption: 'Cases and what accounts owe on them',
    emptyHeading: 'Nothing to settle',
    emptyDetail: 'Cases appear here as soon as they are taken in.',
    markPaid: 'Mark paid',
    hold: 'Hold',
    // Taking a hold off, which is not Release: since 17 October 2026 Release lets a case ship unpaid.
    releaseHold: 'Release hold',
    /*
     * Released to be shipped before it is paid, Released and In ceramic, and
     * ticking many cases to mark them paid or release them. The lab's request
     * of 17 October 2026, items 8, 9, 10 and 14.
     */
    releaseToShip: 'Release',
    takeBack: 'Take back',
    takeBackRelease: 'Take back release',
    released: (jo: string) => `${jo} released to be shipped unpaid`,
    releaseTakenBack: (jo: string) => `${jo} is no longer released`,
    releasedNote: 'Released to ship unpaid',
    tabReleased: 'Released',
    tabCeramic: 'In ceramic',
    releasedColumn: 'Released',
    withWhom: 'At Ceramic with',
    releasedEmptyHeading: 'Nothing released',
    releasedEmptyDetail: 'A case released to be shipped before it is paid is listed here until it is paid.',
    ceramicEmptyHeading: 'Nothing at Ceramic',
    ceramicEmptyDetail: 'Every case with a kit at Ceramic is listed here, assigned or not.',
    agedOnly: (age: string) => `Only the cases received ${age} ago.`,
    everyAge: 'Show every age',
    manyDone: (count: number, verb: string) => `${count === 1 ? '1 case' : `${String(count)} cases`} ${verb}`,
    verbPaid: 'marked paid',
    verbToBePaid: 'to be paid again',
    verbReleased: 'released to be shipped unpaid',
    verbNotReleased: 'no longer released',
    paidOn: 'paid',
    markedPaid: 'marked paid',
    holdReleased: 'hold released',
    putOnHold: 'put on hold',
    unit: 'unit',
    units: 'units',
    /*
     * Accounts' lists, the receipt read before a case is marked paid, the
     * confirmation, and Undo. Request of 5 October 2026. Three lists then; the
     * fourth, Reviewed, is further down with the changes it reads.
     */
    tabsLabel: 'Payment lists',
    tabToPay: 'To be paid',
    tabChanged: 'Changed',
    tabPaid: 'Paid cases',
    paidEmptyHeading: 'No paid cases yet',
    paidEmptyDetail: 'A case marked paid moves here, and can be marked unpaid again from its receipt.',
    changedEmptyHeading: 'Nothing changed',
    changedEmptyDetail: 'A case edited after it was taken in appears here, so its bill can be checked.',
    whatChanged: 'What changed',
    changedOn: 'Changed',
    /*
     * What changed on a case, field by field, and the review of it. The request
     * of 5 October 2026 (third), section 3: Review on a Changed row opens the
     * changes, which is where the case is marked as reviewed; the toast after
     * it carries Undo; and the fourth list, Reviewed, keeps Mark as not
     * reviewed for a mistake found later.
     *
     * `was` and `now` are said before the old and the new value to a screen
     * reader, so the difference never rests on a line through one and a tint
     * behind the other. The field names are keyed by the server's own keys, in
     * its order; a key this build has not heard of is printed as it arrives.
     */
    review: 'Review',
    whatChangedButton: 'What changed',
    changesTitle: (jobOrderNo: string) => `What changed · ${jobOrderNo}`,
    changesLoading: 'Reading what changed',
    changedBy: (name: string | null, when: string) =>
      name === null ? when : `${name} · ${when}`,
    was: 'was',
    now: 'now',
    hiddenChange: 'Changed (hidden)',
    legacyNote: 'Changed before old values were recorded.',
    showAll: 'Show all',
    showLess: 'Show less',
    changeField: {
      doctor: 'Doctor',
      patient: 'Patient',
      origin: 'Origin',
      input: 'Input type',
      item: 'Item',
      shade: 'Shade',
      instructions: 'Instructions',
      translucency: 'Translucency',
      anatomy: 'Anatomy',
      incisal: 'Incisal transparency',
      occlusal: 'Occlusal stain',
      ingot: 'Ingot press',
      block: 'Zircon block',
      stain: 'Stain',
      itemsReceived: 'Items received',
      teeth: 'Teeth',
      units: 'Units',
      span: 'Bridge or bar',
      value: 'Case value',
    } satisfies Record<ChangeField, string>,
    reviewStatement: (jobOrderNo: string) => `Mark ${jobOrderNo} as reviewed?`,
    markReviewed: 'Mark as reviewed',
    markedReviewed: (jobOrderNo: string) => `${jobOrderNo} marked as reviewed`,
    reviewUndone: (jobOrderNo: string) => `${jobOrderNo} is waiting for review again`,
    tabReviewed: 'Reviewed',
    reviewedColumn: 'Reviewed',
    reviewedBy: (name: string, day: string) => `Reviewed by ${name} on ${day}`,
    reviewedEmptyHeading: 'Nothing reviewed yet',
    reviewedEmptyDetail:
      'A case marked as reviewed moves here, and can be marked as not reviewed again from What changed.',
    notReviewedNow: 'This case is not marked as reviewed any more.',
    markNotReviewed: 'Mark as not reviewed',
    notReviewedStatement: (jobOrderNo: string) =>
      `Mark ${jobOrderNo} as not reviewed? It goes back to Changed.`,
    markedNotReviewed: (jobOrderNo: string) => `${jobOrderNo} marked as not reviewed`,
    /*
     * Changes that need no review. The request of 7 October 2026, section 5: only a
     * change to an item, a quantity or an amount goes to Accounts, and the cases
     * sent before that for any edit at all can be cleared by management.
     */
    changesToClear: (count: number) =>
      count === 1
        ? '1 case is waiting though nothing on its bill changed: no item, quantity or amount.'
        : `${String(count)} cases are waiting though nothing on their bills changed: no item, quantity or amount.`,
    clearChanges: 'Clear them',
    confirmClearChanges: (count: number, pending: number) =>
      `Clear ${String(count)} of the ${String(pending)} cases waiting for review? They changed no item, quantity or amount, so they leave the Changed list without being marked reviewed.`,
    changesCleared: (count: number) =>
      count === 1 ? '1 case cleared from Changed' : `${String(count)} cases cleared from Changed`,
    receipt: 'Receipt',
    viewReceipt: 'Receipt',
    receiptTitle: 'Receipt',
    receiptLead: 'Every item on the case, its teeth and its price. A price changed here is for this case only; the price list stays as it is.',
    receiptItem: 'Item',
    receiptTeeth: 'Teeth',
    receiptQty: 'Qty',
    receiptPrice: 'Price',
    receiptValue: 'Value',
    receiptTotal: 'Total',
    receiptPerSpan: 'per span',
    receiptNoTeeth: 'Beyond the chart',
    receiptWas: 'was',
    receiptPriceOf: 'Price of',
    receiptNotCharged: 'A remake the lab does not charge for. Every line is free.',
    receiptPaidOn: 'Paid on',
    receiptLoading: 'Reading the receipt',
    savePrices: 'Save prices',
    unsavedPrices: 'Discard the prices you changed?',
    pricesSaved: 'prices saved for this case',
    badPrice: 'A price is a number, zero or more.',
    close: 'Close',
    confirmTitle: 'Mark paid?',
    confirmStatement: (jobOrderNo: string, total: string) =>
      `Mark ${jobOrderNo} paid for ${total}? It moves to Paid cases.`,
    confirmSavesFirst: 'The prices you changed are saved first.',
    confirmPaid: 'Yes, mark paid',
    back: 'Back',
    paymentUndone: 'is to be paid again',
    markUnpaid: 'Mark unpaid',
    unpaidTitle: 'Mark unpaid?',
    unpaidStatement: (jobOrderNo: string) =>
      `Take the payment off ${jobOrderNo}? It goes back to To be paid.`,
    confirmUnpaid: 'Yes, mark unpaid',
  },

  shipping: {
    /*
     * The pill every screen reads a case's shipping from. Section 7 of the
     * request of 13 September 2026: the same words on the shipping screen, the
     * orders list, the archive, the logistics column and the case record.
     */
    onHold: 'On hold',
    pendingDispatch: 'Pending dispatch',
    awaitingWaybill: 'Awaiting waybill',
    shipped: 'Shipped',
    outForDeliveryBadge: 'Out for delivery',
    receivedBadge: 'Received',
    notShippedYet: 'Not shipped yet',
    /** The two dates on the row, and how long a case has been waiting for its code. */
    dispatchedAt: 'Dispatched',
    receivedAt: 'Received',
    collected: 'Collected',
    collectedToday: 'today',
    collectedYesterday: 'yesterday',
    collectedDaysAgo: 'days ago',
    label: 'Logistics',
    title: 'Shipping & dispatch',
    /*
     * The bar over the groups, section 8.1 of the request of 16 September 2026.
     * The screen already holds every active order, so all of this is in memory.
     */
    filtersLabel: 'Shipping filters',
    search: 'Search',
    searchPlaceholder: 'Job order, doctor, patient, carrier or waybill',
    carrier: 'Carrier',
    allCarriers: 'All carriers',
    origin: 'Origin',
    allOrigins: 'All origins',
    /** What a tab says when the search emptied it. */
    noneMatch: 'Nothing here matches.',
    hint: 'Every case from the moment its work is finished until the customer has it, a tab for each step, with the figures for all of them above. A case on hold cannot be dispatched.',
    /*
     * The tabs, one for each place a case stands between finished and received.
     * The lab's request of 22 October 2026.
     */
    tabsLabel: 'Shipping lists',
    figuresLabel: 'Shipping in figures',
    waitingForAccounts: 'Waiting for Accounts',
    waitingHint: 'Finished, and waiting for Accounts to mark them paid or release them before they can be dispatched.',
    noneWaiting: 'Nothing is waiting for Accounts.',
    noneInTransit: 'Nothing is on its way.',
    receivedHint: 'Received in the last 14 days. Earlier ones are in the Archive.',
    noneReceived: 'Nothing was received in the last 14 days.',
    noneHeld: 'Nothing is on hold.',
    readyToDispatch: 'Ready to dispatch',
    awaitingTracking: 'Awaiting tracking number',
    noneAwaiting: 'Nothing is waiting for a waybill code.',
    addTracking: 'Add tracking number',
    inTransit: 'In transit',
    // The group of cases the customer has. It read "Delivered" until the request
    // of 5 October 2026 (third), section 11, gave that state one word everywhere.
    receivedGroup: 'Received',
    markReceived: 'Mark received',
    receivedBy: 'Received by',
    labDelivery: 'Lab delivery',
    driver: 'Driver',
    edit: 'Edit',
    methodCourier: 'Courier',
    methodLab: 'Lab delivery',
    // The fifth choice, and the name it asks for. Section 8.2.
    otherCarrier: 'Which courier?',
    otherCarrierPlaceholder: 'e.g. Emirates Post',
    handedOverOn: 'Handed over on',
    noneReady: 'No paid or released cases waiting to ship.',
    dispatch: 'Dispatch',
    readyAwaitingPayment: 'Ready to Deliver · awaiting payment',
    // Released by Accounts to be shipped before it is paid. The lab's request of 17 October 2026, item 8.
    releasedUnpaid: 'Released, not paid',
    dispatchedVia: 'dispatched via',
    outForDelivery: 'out for delivery',
    receivedOn: 'Received on',
    trackedOn: 'Waybill code added on',
    noCodeYet: 'no code yet',
    // Adding the code the morning after.
    trackingTitle: 'Add tracking number',
    trackingLead: 'Type the code printed on the waybill, exactly as it reads.',
    trackingCode: 'Waybill code',
    trackingSave: 'Save code',
    trackingSaved: 'waybill code added',
    // Marking it received.
    receivedTitle: 'Mark received',
    receivedLead: 'The day the customer had it, and who signed for it if you know.',
    receivedByLabel: 'Received by (optional)',
    receivedByPlaceholder: 'e.g. clinic reception',
    receivedConfirm: 'Mark received',
    receivedSaved: 'marked received',
    // After Undo on that toast: the mark is gone and the case reads Shipped again.
    receivedUndone: (jo: string) => `${jo} is no longer marked received`,
    // Correcting it later.
    editTitle: 'Edit shipping',
    editSave: 'Save changes',
    editSaved: 'shipping updated',
    nothingChanged: 'Nothing has changed.',
    cancel: 'Cancel',
    // The server's refusals, worded once in the domain.
    held: SHIPPING_REFUSAL.held,
    notReady: SHIPPING_REFUSAL.notReady,
    chooseCourier: SHIPPING_REFUSAL.chooseCourier,
    labHasNoCode: SHIPPING_REFUSAL.labHasNoCode,
    notDispatched: SHIPPING_REFUSAL.notDispatched,
    arrivesBeforeLeaves: SHIPPING_REFUSAL.arrivesBeforeLeaves,
    codeTooShort: SHIPPING_REFUSAL.codeTooShort,
    alreadyReceived: SHIPPING_REFUSAL.alreadyReceived,
  },

  hold: {
    title: 'Put on hold',
    lead: 'The case stays in the lab and cannot be dispatched until someone releases the hold.',
    reason: 'Reason',
    notePlaceholder: 'e.g. invoice unpaid since 10 Jul, called the clinic twice',
    noteLabel: 'Note (optional)',
    confirm: 'Put on hold',
    cancel: 'Cancel',
    pickReason: 'Pick a reason first',
    reasons: [
      'Payment not received',
      'Part payment only',
      'Account over credit limit',
      'Dentist asked to delay',
    ],
  },

  dispatch: {
    title: 'Dispatch',
    lead: 'Say how the case leaves. A courier waybill code can be added later, and a lab delivery never has one.',
    method: 'How it leaves',
    carrier: 'Courier',
    code: 'Waybill code (leave empty if it comes later)',
    codePlaceholder: 'Type the code from the waybill',
    driver: 'Driver (optional)',
    driverPlaceholder: 'e.g. Ahmed',
    noteLabel: 'Note (optional)',
    notePlaceholder: 'e.g. collected 4:30 PM, receipt with reception',
    confirm: 'Confirm dispatch',
    cancel: 'Cancel',
    heldFirst: 'is on hold. Release the hold first.',
  },

  finalCheck: {
    /** The pair that decides a case, named for a screen reader. Section 9. */
    // Section 11.2 of the request of 20 September 2026.
    passingAsSupervisor: 'Passing as supervisor',
    decisionLabel: 'Pass this case or send it back',
    label: 'Stage',
    title: 'Final Check',
    hint: 'Every case passes here before it is Ready to Deliver.',
    caption: 'Cases in Final Check',
    // The case screen's field labels.
    kit: 'Kit',
    input: 'Input',
    emptyHeading: 'Nothing waiting for Final Check',
    emptyDetail: 'Every case has been checked. Cases arrive here from Ceramic.',
    kits: 'kits',
    kitSingular: 'kit',
    stats: {
      kits: 'Kits',
      overdue: 'Overdue',
      oldest: 'Oldest wait',
      rework: 'Rework',
    },
    back: 'Back to Final Check',
    pass: 'Pass QC',
    sendToRework: 'Send to rework',
    passed: 'passed Final Check, now Ready to Deliver',
    sentToRework: 'sent to',
    forRework: 'for rework',
    reworkTitle: 'Send to rework',
    reworkLead: 'Failed Final Check. Pick the department that reworks it. The case stays flagged until it passes Final Check.',
    reworkConfirm: 'Send to rework',
    reworkNoteRequired: 'Say what failed before sending it back',
    notInFinalCheck: 'That case is not in Final Check.',
    // The three facts that change the decision, above the record. Section 4.4
    // of the request of 16 September 2026.
    waiting: 'Waiting here',
    returned: 'Has been sent back before',
    /*
     * The work on the case, above its record. The request of 7 October 2026,
     * section 4: when the lab received it, how long it has had it, and who
     * worked it in order, one line a step.
     */
    work: {
      title: 'Work on this case',
      received: 'Received',
      inLab: 'In the lab',
      worked: 'Time worked',
      sentBack: 'Sent back',
      flags: (count: number) => (count === 1 ? '1 flag' : `${String(count)} flags`),
      reworks: (count: number) => (count === 1 ? '1 rework' : `${String(count)} reworks`),
      none: 'Nobody has worked on this case yet.',
      stepsLabel: 'The steps of the case, oldest first',
      showAll: 'Show all details',
      hideAll: 'Hide details',
      nobody: 'Nobody recorded',
      to: 'to',
      open: 'in progress',
      noTime: 'no time recorded',
      // The marks on a line.
      flagged: 'Flagged',
      failed: 'Failed Final Check',
      afterFlag: 'After a flag',
      rework: 'Rework',
      // The details a line opens to.
      department: 'Department',
      technician: 'Technician',
      started: 'Started',
      finished: 'Finished',
      notFinished: 'Not finished yet',
      timeWorked: 'Time worked',
      startToFinish: 'Start to finish',
      why: 'Why',
      whyRework: 'Sent back from Final Check to be redone',
      whyFlag: 'Sent here by a flag',
      reasonFlag: 'Flag reason',
      reasonRework: 'Why it failed',
      // What the system wrote as it closed the step: taken over, moved, archived.
      note: 'Note',
    },
  },

  archive: {
    label: 'Records',
    title: 'Archive',
    hint: 'Every case that has left the lab, Shipped until somebody marks it Received. Search by anything, sort by any column, export the current filter.',
    caption: 'Archived cases',
    search: 'Search doctor, patient, job order, product or tracking',
    searchPlaceholder: 'e.g. Dr. Doe, JO-1620, Zircon Crown',
    filtersLabel: 'Filter the archive',
    origin: 'Origin',
    allOrigins: 'All origins',
    /*
     * The request of 5 October 2026 (third), section 11. The day a case left the
     * lab is its Shipped day: the column and the date filter read "Delivered"
     * before, beside a Received column, for a case the customer did not yet
     * have. The Status filter is Shipped or Received, whose two words are the
     * domain's ARCHIVE_STATUS_LABEL, and In Shipped counts whole lab days.
     */
    shippedOn: 'Shipped on',
    status: 'Status',
    allStatuses: 'All',
    inShipped: 'In Shipped',
    daysInShipped: (days: number) => (days === 1 ? '1 day' : `${String(days)} days`),
    carrier: 'Carrier',
    allCarriers: 'All carriers',
    exportCsv: 'Export the current filter',
    exporting: 'Exporting',
    exported: 'archived cases exported',
    exportFailed: 'The export could not be produced',
    item: 'Item',
    units: 'Units',
    value: 'Value',
    received: 'Received',
    tracking: 'Tracking',
    counted: 'archived cases',
    countedOne: 'archived case',
    emptyHeading: 'Nothing matches that search',
    emptyDetail: 'Try a shorter search, a wider date range, or all origins.',
  },

  record: {
    back: 'Archive',
    label: 'Archive record',
    liveLabel: 'Job order',
    items: 'Items',
    shade: 'Shade',
    bridges: 'Bridges & bars',
    delivered: 'delivered',
    caseDetails: 'Case details',
    status: 'Status',
    dispatched: 'Dispatched',
    doctor: 'Doctor',
    patient: 'Patient',
    product: 'Product',
    units: 'Units',
    origin: 'Origin',
    deliveredOn: 'Delivered',
    value: 'Value (price list)',
    carrier: 'Carrier',
    tracking: 'Tracking',
    dispatchedOn: 'Dispatched',
    received: 'Received',
    notReceived: 'Not yet',
    receivedBy: 'by',
    // Who in the lab marked it received, and when. The request of 5 October 2026
    // (third), section 11. A case marked before the name was kept has the time alone.
    markedReceived: 'Marked received',
    markedReceivedBy: (name: string | null, when: string) =>
      name === null ? when : `${name} · ${when}`,
    labDelivery: 'Lab delivery',
    dispatchedHint: 'dispatched',
    receivedHint: 'received',
    printSheet: 'Print job sheet (A4)',
    teeth: 'Teeth',
    photos: 'Case photos',
    noPhotos: 'No photos were attached to this case.',
    // One history for the case: the bench entries and every action, together.
    caseHistory: 'Case history',
    filterAll: 'Everything',
    filterMoves: 'Moves',
    filterNotes: 'Notes',
    nowAt: 'Now at',
    filterBench: 'Tray work',
    filterAccounts: 'Accounts and shipping',
    filterEdits: 'Edits',
    filterLabel: 'Show in the case history',
    showWholeHistory: 'Show the whole history',
    noHistoryYet: 'Nothing has been recorded against this case.',
    noHistoryForFilter: 'Nothing of this kind has happened to this case.',
    loadingHistory: 'Loading the case history',
    stillHere: 'Still in this stage',
    activeTime: 'active',
    by: 'by',
    unassigned: 'Unassigned',
    notFound: 'That case is not in the archive.',
    // The same screen serves a live case. These replace the delivery wording.
    inProduction: 'In production',
    now: 'now at',
    backToOrders: 'Orders',
  },

  find: {
    title: 'Find a case',
    lead: 'Type a job order, kit or QR code, or scan the tray.',
    // For somebody who cannot open the scanner, and so is not offered it.
    leadTyped: 'Type a job order, kit or QR code.',
    inputLabel: 'Job order, kit or QR code',
    placeholder: 'JO-2054, K-1093 or QR-47013',
    open: 'Open',
    scan: 'Scan instead',
    close: 'Close',
    unknown: 'That code is not in the system.',
  },

  reports: {
    label: 'Sales',
    noMoney: 'The sales report shows money, which needs the Mark paid or administration permission.',
    title: 'Units sold & best sellers',
    hintAll: 'Delivered units since the lab began, by price list item',
    hintToday: 'Delivered units today, by price list item',
    hintWeek: 'Delivered units over the last 7 days, by price list item',
    hintMonth: 'Delivered units over the last 30 days, by price list item',
    // All time first and by default. The lab's request of 17 October 2026, item 4.
    rangeAll: 'All time',
    rangeToday: 'Today',
    rangeWeek: 'Week',
    rangeMonth: 'Month',
    rangeLabel: 'Reporting range',
    dentist: 'Dentist',
    allDentists: 'All dentists',
    origin: 'Origin',
    allOrigins: 'All origins',

    unitsSold: 'Units sold',
    versusPrevious: 'vs previous',
    noPrevious: 'no previous period',
    bestSeller: 'Best selling item',
    ordersDelivered: 'Orders delivered',
    valueTotal: 'Value (price list)',
    itemsSold: 'Items sold',
    ofCatalogue: 'of the catalogue',
    averageUnit: 'Average unit value',

    byItem: 'Units sold by item',
    byItemSub: 'Ranked by units, price list names',
    byCategory: 'Units by category',
    byCategorySub: 'The five catalogue categories',
    byDentist: 'Units by dentist',
    byDentistSub: 'Who is sending the work',
    byOrigin: 'Units by origin',
    byOriginSub: 'Local, international and online',

    production: 'Production reports',
    productionSub: 'Operational reporting for the selected range',
    active: 'Active cases',
    due: 'Due',
    overdue: 'Overdue',
    onTime: 'On time',
    turnaround: 'Average turnaround',
    completed: 'Completed',
    days: 'd',

    noData: 'No sales in this range.',
    noSales: 'Nothing was delivered in this range',
    noSalesDetail: 'Widen the range, or clear the dentist and origin filters.',
  },

  activity: {
    label: 'Audit',
    title: 'Activity log',
    hint: 'Every action across the floor, by user. Newest first.',
    byUser: 'Filter by user',
    allUsers: 'All users',
    type: 'Activity type',
    allTypes: 'All activity',
    counted: 'entries',
    countedOne: 'entry',
    empty: 'No activity for this filter',
    emptyDetail: 'Clear the user, the type or the case and look again.',
    system: 'The system',
    byCase: 'Case',
    casePlaceholder: 'JO-2054',
  },

  /*
   * Booking a returned case in as a remake. It used to be a button on the
   * archive record that copied the original; section 7 of the request of
   * 16 September 2026 moved it to intake and copies nothing, because the case
   * arrives back in a box and somebody at reception books it in.
   */
  redo: {
    isRedo: 'Is this a remake?',
    isRedoHint: 'The case came back from the dentist and is being made again.',
    findCase: 'Which case?',
    findPlaceholder: 'Search by job order, doctor or patient',
    searching: 'Looking',
    noMatches: 'Nothing in the archive matches that.',
    picked: 'Remaking',
    change: 'Change',
    delivered: 'delivered',
    reasonLabel: 'Why did it come back?',
    reasonPlaceholder: 'e.g. chipped margin at 14, the dentist sent it back',
    urgent: 'Urgent',
    urgentHint: 'Urgent cases are shown first on every list and marked red.',
    sentBackAs: 'Sent back as',
    redoOf: 'Redo of',
    returned: 'Returned',
    opened: 'opened as a redo of',
    // The server's refusals, worded once in the domain.
    reasonRequired: REDO_REFUSAL.reasonRequired,
    reasonTooLong: REDO_REFUSAL.reasonTooLong,
    notInArchive: REDO_REFUSAL.notInArchive,
    notAllowed: REDO_REFUSAL.notAllowed,
    /*
     * A remake of a case from before this system. The request of 6 October 2026
     * (second), item 1. Its original is a number somebody typed and no case
     * here, so wherever a remake names its original a legacy one says so and
     * is plain text, never a link: `redoOriginalInSystem` in the domain is the
     * rule, and this is the wording.
     */
    beforeSystem: '(before this system)',
    original: (original: string, legacy: boolean) =>
      legacy ? `Redo of ${original} (before this system)` : `Redo of ${original}`,
    legacyNumberRequired: REDO_REFUSAL.legacyNumberRequired,
    legacyNumberTooLong: REDO_REFUSAL.legacyNumberTooLong,
    legacyInSystem: REDO_REFUSAL.legacyInSystem,
    /*
     * The choice the remake block offers once it is ticked, and the one field
     * the second choice asks for. The pair is named for a screen reader.
     */
    originalWhere: 'Where the original case is',
    originalInArchive: 'The original is in the archive',
    originalBeforeSystem: 'The original is from before this system',
    legacyNumber: 'Job order number of the original',
    legacyNumberPlaceholder: 'As written on the old job order',
  },

  // Deleting a case, with approval, for thirty days.
  deletion: {
    delete: 'Delete case',
    request: 'Ask for deletion',
    title: 'Delete case',
    leadDelete:
      'The case leaves every list now. It can be restored from the Archive for 30 days, and then it is gone for good.',
    leadRequest:
      'Somebody who approves deletions has to agree first. Until then the case carries on as before.',
    reasonLabel: 'Why is this case being deleted?',
    reasonPlaceholder: 'e.g. opened twice at intake, this one is the duplicate',
    cancel: 'Cancel',
    sending: 'Sending',
    pendingBy: 'Deletion asked for by',
    on: 'on',
    /*
     * Approve, not Approve deletion. The row already says which case and the
     * heading already says it is a deletion, and the shorter pair is what keeps
     * both buttons on one line. Section 9.3 of the request of 16 September 2026.
     */
    approve: 'Approve',
    refuse: 'Refuse',
    // Section 11.2 of the request of 20 September 2026.
    passingAsSupervisor: 'Passing as supervisor',
    decisionLabel: 'Approve or refuse this deletion',
    withdraw: 'Withdraw',
    restore: 'Restore',
    /*
     * Deleting a case for good, ahead of its purge day. Request of 24 September
     * 2026: "add an option to permanently delete a case. Before this ask for
     * approval." The approval is the dialog these words are on.
     */
    purge: 'Delete permanently',
    purgeTitle: 'Delete permanently',
    purgeLead:
      'The case, its tray, its photographs and its history leave the system now instead of on the purge day. Nothing can restore it afterwards.',
    purgeConfirm: 'Delete permanently',
    purgedToast: 'deleted for good',
    purgesIn: 'Purges in',
    day: 'day',
    days: 'days',
    purgesToday: 'Tonight',
    notScheduled: 'Not until approved',
    deletedOn: 'Deleted on',
    deletedBy: 'Deleted by',
    askedBy: 'Asked by',
    by: 'by',
    purgesOn: 'purges on',
    tabsLabel: 'Archive sections',
    tabDelivered: 'Delivered',
    tabDeleted: 'Deleted',
    awaitingApproval: 'Awaiting approval',
    deleted: 'Deleted',
    waitingBanner: 'A deletion is waiting for approval',
    waitingOnly: 'Waiting for approval only',
    search: 'Search the deleted cases',
    searchPlaceholder: 'Job order, doctor, patient or item',
    filtersLabel: 'Filter the deleted cases',
    caption: 'Deleted cases',
    loading: 'Loading the deleted cases',
    emptyHeading: 'Nothing deleted',
    emptyDetail:
      'A deleted case waits here for 30 days before it is purged, and a deletion waiting for approval waits here too.',
    deletedToast: 'deleted. It can be restored from the Archive for 30 days.',
    requestedToast: 'deletion asked for',
    approvedToast: 'deleted',
    refusedToast: 'deletion refused',
    withdrawnToast: 'deletion withdrawn',
    restoredToast: 'restored',
    // The server's refusals, worded once in the domain.
    alreadyDeleting: DELETION_REFUSAL.alreadyDeleting,
    ownApproval: DELETION_REFUSAL.ownApproval,
    nothingPending: DELETION_REFUSAL.nothingPending,
    notDeleted: DELETION_REFUSAL.notDeleted,
    dispatchPending: DELETION_REFUSAL.dispatchPending,
    reasonRequired: DELETION_REFUSAL.reasonRequired,
  },

  /*
   * Acting on a selection of cases. Section 4 of the request of 24 September
   * 2026: "allow option to select multiple cases in orders, archive, production
   * line, and allow different actions for the selected cases like: delete all,
   * archive all, assign all to a particular department or person".
   */
  bulk: {
    movedBack: 'Moved back where they were',
    // The box on every row, and the one in the header for the page.
    select: 'Select',
    selectCase: 'Select',
    selectPage: 'Select every case on this page',
    barLabel: 'Actions on the selected cases',
    selectedOne: 'case selected',
    selectedMany: 'cases selected',
    pageToo: 'Select this page too',
    clear: 'Clear',
    cancel: 'Cancel',
    close: 'Close',
    sending: 'Sending',

    assign: 'Assign',
    assignTitle: 'Assign',
    assignLead:
      'Send them to a department, give them to a person, or both. A person is given them at the department they end up at.',
    department: 'Department',
    whereTheyAre: 'Leave them where they are',
    person: 'Person',
    nobody: 'Nobody in particular',
    pickDepartmentFirst: 'These cases are at different departments. Choose one to choose a person there.',
    loadingPeople: 'Loading who works there',
    nobodyThere: 'Nobody works that department yet.',
    chooseSomething: BULK_REFUSAL.chooseSomething,
    assigned: 'assigned',

    archive: 'Archive',
    archiveTitle: 'Archive',
    archiveLead:
      'They leave Orders, the production line and every queue and tray, and go to the Archived tab. Nothing is deleted, and they can be taken out again exactly as they were.',
    archiveReasonLabel: 'Why? (optional)',
    archiveReasonPlaceholder: 'e.g. delivered by hand before the system went live',
    archived: 'archived',

    unarchive: 'Take out of the archive',
    unarchived: 'taken out of the archive',

    delete: 'Delete',
    deleteTitle: 'Delete',
    deleteReasonLabel: 'Why are they being deleted?',
    askToDelete: 'Ask for deletion',
    deleted: 'deleted',
    // A request per case, waiting for somebody who approves deletions.
    deletionAsked: 'sent for approval to delete',

    // The answer, when some cases could not be moved.
    resultTitle: 'Not all of them',
    resultOf: 'of',
    resultDone: 'done.',
    resultNone: 'None of them could be done.',
    notDone: 'Not done',
    one: 'case',
    many: 'cases',
  },

  /*
   * The archive's Archived tab and an archived case. Section 4 of the request of
   * 24 September 2026.
   */
  archived: {
    tab: 'Archived',
    search: 'Search the archived cases',
    searchPlaceholder: 'Job order, doctor, patient or item',
    filtersLabel: 'Filter the archived cases',
    archivedOn: 'Archived',
    caption: 'Archived cases',
    loading: 'Loading the archived cases',
    emptyHeading: 'Nothing archived',
    emptyDetail:
      'A case archived from Orders or the production line waits here, off every list of work, until it is taken out again.',
    recentlyArchived: 'Recently archived',
    colItem: 'Item',
    colStoppedAt: 'Stopped at',
    colArchived: 'Archived',
    colBy: 'By',
    colReason: 'Why',
    banner: 'This case is archived',
    bannerBy: 'by',
    bannerOn: 'on',
    notice: ARCHIVE_REFUSAL.isArchived,
    isArchived: ARCHIVE_REFUSAL.isArchived,
  },

  // Editing a case after intake. System test handover 2.4.
  editCase: {
    /*
     * The photographs, which are not part of the form: a file is its own
     * request, so each one takes effect as it is chosen or dropped. Saying so
     * beats a Save button that quietly means two different things.
     */
    photos: 'Case photographs',
    photosHint:
      'What arrived in the box. Adding or removing one takes effect straight away, not on Save.',
    photosInputLabel: 'Choose photographs of the case',
    photosAdd: 'Add photographs',
    photosAddMore: 'Add more',
    photosAdding: 'Uploading',
    photosRemove: 'Remove photograph',
    photoRemoved: 'Photograph removed',
    label: 'Edit case',
    hint: 'Change what was entered at intake. Start and delivery dates have their own control on the order.',
    loading: 'Loading the case',
    back: 'Back to the job order',
    save: 'Save changes',
    saving: 'Saving',
    cancel: 'Cancel',
    saved: 'saved',
    nothing: 'Nothing has changed yet.',
    notAllowed: 'You do not have access to edit this case.',
  },
  items: {
    label: 'Price list',
    title: 'Items',
    hint: 'What the lab makes and what it charges. A new price applies to new cases only, and a retired item stays on the cases that used it.',
    loading: 'Loading the price list',
    addTitle: 'Add an item',
    name: 'Item',
    namePlaceholder: 'e.g. Zircon Veneer',
    nameRequired: 'Give the item a name',
    category: 'Category',
    newCategory: 'New category…',
    newCategoryName: 'New category name',
    newCategoryPlaceholder: 'e.g. Implants',
    price: 'Price',
    priceInvalid: 'Enter a price of zero or more',
    kind: 'Kind',
    kindUnit: 'Unit',
    kindSpan: 'Span',
    add: 'Add item',
    adding: 'Adding',
    added: 'added to the price list',
    save: 'Save',
    saved: 'saved',
    retire: 'Retire',
    restore: 'Put back',
    retired: 'Retired',
    active: 'In use',
    retiredToast: 'retired. It stays on the cases that used it.',
    restoredToast: 'is back on the price list',
    status: 'Status',
    actions: 'Actions',
    nameFor: 'Name of',
    priceFor: 'Price of',
    caption: 'Items in',
    conflict: 'Somebody else changed this item while you were editing. It has been reloaded.',
    empty: 'No items in this category yet',
  },
  /*
   * The counts above a list, each a card that narrows the list under it when
   * it is pressed. The request of 6 October 2026 (fifth), section 1.
   */
  kpi: {
    doctorsLabel: 'Doctors in numbers',
    patientsLabel: 'Patients in numbers',
    total: 'Total',
    newThisWeek: 'New this week',
    newThisMonth: 'New this month',
    returning: 'Returning',
    withActiveCases: 'With active cases',
    // Not a card: the list as the notice about the conversion narrows it.
    needsReview: 'Needs review',
    /** Beside the count while the list is narrowed: "Filter: Returning". */
    filterOn: (label: string) => `Filter: ${label}`,
    clearFilter: 'Clear filter',
  },
  /*
   * A doctor's address, in the parts its country's post asks for. The request
   * of 6 October 2026 (fifth), section 2. What the server refuses a part with
   * is worded in src/domain/geo and shown as it arrives.
   */
  address: {
    title: 'Address',
    lead: 'Leave it empty, or fill it in whole: once any part is entered, the city, the first line and the clinic are needed too.',
    loading: 'Loading the address fields',
    loadFailed: 'The list of countries could not be loaded.',
    country: 'Country',
    /** What a country calls its first level division, as the field's label. */
    region: {
      province: 'Province',
      state: 'State',
      island: 'Island',
      department: 'Department',
      parish: 'Parish',
      county: 'County',
      oblast: 'Oblast',
      area: 'Area',
      prefecture: 'Prefecture',
      district: 'District',
      do_si: 'Do / Si',
    },
    city: 'City',
    area: 'Area / district',
    line1: 'Address line 1',
    line1Hint: 'Building and street.',
    line2: 'Address line 2',
    line2Hint: 'Floor, office or unit.',
    /** What a country calls its postal code. A country with none has a PO box. */
    postal: {
      postal: 'Postal code',
      zip: 'ZIP code',
      pin: 'PIN code',
      eircode: 'Eircode',
      pobox: 'PO box',
    },
    /** Under the postal code, one its country's post would accept: "For example 95014." */
    example: (code: string) => `For example ${code}.`,
    poBoxHint: 'The number alone.',
    typedHint: 'Choose from the list, or type it and use it as typed.',
    preview: 'On a label',
    clear: 'Clear the address',
    needsReview: 'Needs review',
    reviewHint:
      'The old text could not be placed with certainty. Check each part against it, then save.',
    // The words of a list that is searched as it is typed in.
    list: {
      clear: (field: string) => `Clear ${field}`,
      /** The last row, for what was typed and is not in the list. */
      useTyped: (text: string) => `Use '${text}'`,
      none: 'Nothing in the list matches.',
      loading: 'Loading the list',
    },
    /** Where the app shows its version history. Section 8, item 7, of the same request. */
    credits:
      'City and region names are from GeoNames, and address formats from the address data Google publishes. Both are licensed CC BY 4.0.',
  },
  /*
   * A telephone number with its country's code. The request of 6 October 2026
   * (fifth), section 2. The refusal of a number is worded in src/domain/clients.
   */
  phone: {
    /** The button that holds the code, as a screen reader names it: "Country code +971". */
    codeButton: (code: string) => `Country code ${code}`,
    codeLabel: 'Country code',
    codePlaceholder: 'Country or code',
    /** Under the number, once it reads as one: "Saved as +971 50 123 4567". */
    savedAs: (number: string) => `Saved as ${number}`,
    prefilled: 'Read from the old text and not saved yet. Check it, then save.',
    needsReview: 'Needs review',
    reviewHint:
      'The old text could not be read as a number with certainty. Enter it with its country code, then save.',
    list: {
      clear: (field: string) => `Clear ${field}`,
      useTyped: (text: string) => `Use '${text}'`,
      none: 'No country matches.',
      loading: 'Loading the country codes',
    },
  },
  /*
   * Doctors and patients: the two lists, the two profiles and their forms. The
   * request of 6 October 2026 (second), items 4 and 5, and the request of
   * 6 October 2026 (third): IDs, one patient for every doctor they are seen
   * by, merging duplicate doctors, and renaming as a permission of its own.
   * What the server refuses a record with is worded in src/domain/clients and
   * shown as it arrives.
   */
  clients: {
    label: 'Doctors and patients',

    // The two lists.
    doctorsTitle: 'Doctors',
    doctorsHint:
      'Every doctor the lab works for: the clinic, how to reach them, their patients and their cases.',
    patientsTitle: 'Patients',
    patientsHint:
      'Every patient, one record each, whichever doctors they are seen by.',
    doctorFiltersLabel: 'Search and order the doctors',
    patientFiltersLabel: 'Search and order the patients',
    searchDoctors: 'Search doctors',
    // An ID can be typed, with or without its letters. The request of 6 October 2026 (third), item 2.
    searchDoctorsPlaceholder: 'Name, clinic, phone, email or ID (DR-0012)',
    searchPatients: 'Search patients',
    searchPatientsPlaceholder: 'Name or ID (PT-00031)',
    // The doctors whose name another doctor's differs from only in a title or in punctuation.
    duplicatesToggle: 'Possible duplicates',
    noDuplicates: 'No possible duplicates',
    noDuplicatesDetail:
      'No two doctors have names that differ only in a title, in spacing or in punctuation.',
    // The four orders, which the server sorts by.
    sortNewest: 'Newest first',
    sortName: 'Name A to Z',
    sortMostCases: 'Most cases',
    sortLatestCase: 'Latest case',
    colId: 'ID',
    colName: 'Name',
    colClinic: 'Clinic',
    // "Phone" since the request of 6 October 2026 (fifth): a number of any country, with its code.
    colPhone: 'Phone',
    colCases: 'Cases (active / all)',
    colPatients: 'Patients',
    colPatient: 'Patient',
    colDoctors: 'Doctors',
    /** After the first two of a patient's doctors: "+3 more". */
    moreDoctors: (more: number) => `+${String(more)} more`,
    /** A patient added by hand, whom no case has tied to a doctor yet. */
    noDoctorYet: 'No doctor yet',
    /** Cases still in hand, then every case: "3 / 41". */
    activeOfAll: (active: number, all: number) => `${String(active)} / ${String(all)}`,
    doctorsCaption: 'Doctors',
    patientsCaption: 'Patients',
    doctorsCounted: (count: number) => (count === 1 ? 'doctor' : 'doctors'),
    patientsCounted: (count: number) => (count === 1 ? 'patient' : 'patients'),
    loadingDoctors: 'Loading the doctors',
    loadingPatients: 'Loading the patients',
    noDoctors: 'No doctors yet',
    noDoctorsDetail: 'A doctor is added here, or by booking in a case under a new name.',
    noDoctorMatches: 'No doctor matches this search',
    noPatients: 'No patients yet',
    noPatientsDetail: 'A patient is added here, or by booking in a case under a new name.',
    noPatientMatches: 'No patient matches this search',
    noMatchDetail: 'Check the spelling, or clear the search to see everybody.',
    // The list narrowed by one of the counts above it, with nobody in it. The
    // request of 6 October 2026 (fifth), section 1.
    noDoctorsShown: 'No doctor is in this group',
    noPatientsShown: 'No patient is in this group',
    noShownDetail: 'Clear the filter to see everybody.',
    /*
     * The two notices above the Doctors list, and the conversion the first of
     * them opens. The same request, section 2, "Existing data": the text typed
     * before numbers and addresses had a form is converted only where that is
     * certain, after management has read what would happen.
     */
    oldFormatNotice: (count: number) =>
      `${String(count)} ${count === 1 ? 'doctor has' : 'doctors have'} a phone number or an address in the old format.`,
    reviewAndConvert: 'Review and convert',
    needsReviewNotice: (count: number) =>
      `${String(count)} ${count === 1 ? 'doctor has' : 'doctors have'} a phone number or an address that needs review.`,
    showThem: 'Show them',
    convertTitle: 'Convert phone numbers and addresses',
    convertLead:
      'What converting the old text would do. Nothing changes until Convert is pressed, and the old text is kept beside the new until each doctor is next saved.',
    convertLoading: 'Working out what would be converted',
    convertCounts: 'What would happen',
    convertDoctors: 'Doctors',
    convertPhonesConverted: 'Phone numbers converted',
    convertPhonesForReview: 'Phone numbers for review',
    convertAddressesConverted: 'Addresses converted',
    convertAddressesForReview: 'Addresses for review',
    convertListLabel: 'Doctors with old text',
    convertNothing: 'Nothing is waiting to be converted',
    convertNothingDetail: 'Every phone number and address is in the new form, or has been looked at.',
    convertPhone: 'Phone',
    convertAddress: 'Address',
    convertBecomes: 'Becomes',
    /** Why a number or an address is left for a person: "Needs review: The country is not named". */
    convertNeedsReview: (reason: string) => `Needs review: ${reason}`,
    convertCertain: 'Certain so far',
    convert: 'Convert',
    close: 'Close',
    /** The one question before the conversion runs, with what it will do. */
    confirmConvert: (counts: {
      doctors: number;
      phonesConverted: number;
      phonesForReview: number;
      addressesConverted: number;
      addressesForReview: number;
    }) =>
      `Convert the old text of ${String(counts.doctors)} ${
        counts.doctors === 1 ? 'doctor' : 'doctors'
      }? Phone numbers: ${String(counts.phonesConverted)} converted, ${String(
        counts.phonesForReview,
      )} left for review. Addresses: ${String(counts.addressesConverted)} converted, ${String(
        counts.addressesForReview,
      )} left for review. The old text is kept.`,
    convertedTitle: 'Conversion done',
    convertedMessage: (counts: {
      phonesConverted: number;
      phonesForReview: number;
      addressesConverted: number;
      addressesForReview: number;
    }) =>
      `Phone numbers: ${String(counts.phonesConverted)} converted, ${String(
        counts.phonesForReview,
      )} need review. Addresses: ${String(counts.addressesConverted)} converted, ${String(
        counts.addressesForReview,
      )} need review. What needs review is marked on the doctor and keeps its old text.`,
    ok: 'OK',
    addDoctor: 'Add doctor',
    addPatient: 'Add patient',

    // The forms, each in a sheet.
    addDoctorTitle: 'Add a doctor',
    addDoctorLead: 'Only the name is needed. The rest can be filled in later.',
    editDoctorTitle: (name: string) => `Edit ${name}`,
    editDoctorLead: 'A new name shows on every case of this doctor.',
    // For somebody who may edit the details and not the name. The request of 6 October 2026 (third), item 4.
    editDetailsLead: 'How the lab reaches this doctor.',
    nameLocked: 'Renaming a doctor needs its own permission.',
    detailsLocked: 'Changing these details needs its own permission.',
    fieldName: 'Name',
    fieldClinic: 'Clinic',
    fieldMobile: 'Mobile',
    // The form's own word for the number since the request of 6 October 2026 (fifth), section 2.
    fieldPhone: 'Phone',
    fieldEmail: 'Email',
    fieldWebsite: 'Website',
    fieldDeliveryAddress: 'Delivery address',
    /** Above a field, while what was typed before it had a form is still kept: the old text, as it was. */
    enteredBefore: (text: string) => `Entered before as: ${text}`,
    doctorNamePlaceholder: 'e.g. Dr. Jane Roe',
    clinicPlaceholder: 'e.g. Roe Smile Centre',
    websitePlaceholder: 'e.g. roesmile.example',
    addPatientTitle: 'Add a patient',
    addPatientLead:
      'Only the name. A patient is one record, and a case ties them to the doctor it is for.',
    renamePatientTitle: (name: string) => `Rename ${name}`,
    renamePatientLead: 'The new name shows on every case of this patient.',
    patientName: 'Patient’s name',
    patientNamePlaceholder: 'e.g. Jane Doe',
    // What the sheet offers once the server has said the name is somebody's already:
    // a second record, for a different person of the same name. The request of
    // 6 October 2026 (third), item 3.
    addSameName: 'Add another patient with this name',
    renameSameName: 'Use this name anyway',
    cancel: 'Cancel',
    save: 'Save',
    discardQuestion: 'Discard what you have typed?',

    // Choosing a doctor from the records, by typing.
    pickerPlaceholder: 'Type a name, a clinic or an ID',
    pickerHint: 'Type at least two letters to search.',
    pickerSearching: 'Searching',
    pickerNone: 'No doctor matches.',
    pickerResults: 'Matching doctors',
    pickerChange: 'Change',
    /** How many cases a doctor has, beside the ID in a list of doctors: "12 cases". */
    casesCounted: (count: number) =>
      count === 0 ? 'No cases' : `${String(count)} ${count === 1 ? 'case' : 'cases'}`,

    // The questions before a record is written, each naming the record.
    confirmAddDoctor: (name: string) => `Add ${name}?`,
    confirmSaveDoctor: (name: string, fields: readonly string[]) =>
      `Save the changes to ${name}: ${fields.join(', ')}?`,
    confirmRenameDoctor: (from: string, to: string, fields: readonly string[]) =>
      `Rename ${from} to ${to}${
        fields.length === 0 ? '' : `, and save the changes to ${fields.join(', ')}`
      }? Every case of this doctor will show the new name.`,
    confirmDeleteDoctor: (name: string) => `Delete ${name}? This cannot be undone.`,
    confirmAddPatient: (name: string) => `Add ${name} as a patient?`,
    confirmAddSameName: (name: string) =>
      `Add another patient named ${name}? There will be two records with this name, for two different people.`,
    confirmRenamePatient: (from: string, to: string) =>
      `Rename ${from} to ${to}? Every case of this patient will show the new name.`,
    confirmRenameSameName: (from: string, to: string) =>
      `Rename ${from} to ${to}, although another patient has that name? There will be two records with this name, for two different people.`,
    confirmDeletePatient: (name: string) => `Delete ${name}? This cannot be undone.`,
    /**
     * The question before one doctor is merged into another, the same on the
     * profile's list of possible duplicates and after choosing any doctor. The
     * request of 6 October 2026 (third), item 1. It names both by name and ID,
     * says what moves and what goes, and that there is no way back:
     * "Merge Dr Ahmed (DR-0007) into Dr. Ahmed Ali (DR-0003)? Its 12 cases
     * move to DR-0003 and DR-0007 is removed. This cannot be undone."
     */
    confirmMerge: (
      duplicate: { name: string; code: string },
      keep: { name: string; code: string },
      cases: number,
    ) =>
      `Merge ${duplicate.name} (${duplicate.code}) into ${keep.name} (${keep.code})? ${
        cases === 0
          ? `It has no cases. ${duplicate.code} is removed.`
          : `Its ${String(cases)} ${cases === 1 ? 'case moves' : 'cases move'} to ${keep.code} and ${
              duplicate.code
            } is removed.`
      } This cannot be undone.`,
    doctorAdded: (name: string) => `${name} added`,
    doctorSaved: (name: string) => `${name} saved`,
    doctorDeleted: (name: string) => `${name} deleted`,
    doctorMerged: (duplicateCode: string, keepCode: string) =>
      `${duplicateCode} merged into ${keepCode}`,
    patientAdded: (name: string) => `${name} added`,
    patientRenamed: (name: string) => `Renamed to ${name}`,
    patientDeleted: (name: string) => `${name} deleted`,

    // The two profiles.
    backToDoctors: 'Back to Doctors',
    backToPatients: 'Back to Patients',
    doctorLabel: 'Doctor',
    patientLabel: 'Patient',
    edit: 'Edit',
    rename: 'Rename',
    deleteRecord: 'Delete',
    deleteDoctorTitle: 'Delete doctor',
    deletePatientTitle: 'Delete patient',
    notSet: 'Not set',
    opensInNewTab: 'opens in a new tab',
    // The number and the address among a doctor's details. The request of 6 October 2026 (fifth), section 2.
    profilePhone: 'Phone',
    profileAddress: 'Address',
    // On a number or an address the conversion could not place with certainty: the old text is what shows.
    needsReview: 'Needs review',
    needsReviewHint: 'Kept as it was typed before. Edit the doctor to set it.',
    statCases: 'Cases',
    statActive: 'Active cases',
    statPatients: 'Patients',
    latestCase: 'Latest case',
    addedOn: 'Added',
    casesHeading: 'Cases',
    // A patient's doctors, on the patient's profile. The request of 6 October 2026 (third), item 3.
    doctorsHeading: 'Doctors',
    seenBy: (count: number) => `Seen by ${String(count)} doctors`,
    noDoctorsOfPatient:
      'No doctor yet. The first case booked in for this patient says which doctor it is for.',
    loadingDoctor: 'Loading the doctor',
    loadingPatient: 'Loading the patient',
    loadingCases: 'Loading the cases',
    noPatientsOfDoctor: 'No patient has a case with this doctor yet.',
    // A doctor's possible duplicates, and merging. The same request, item 1.
    duplicatesHeading: 'Possible duplicates',
    duplicatesHint:
      'Doctors whose name differs from this one only in a title, in spacing or in punctuation.',
    noSimilar: 'No other doctor has a name like this one.',
    loadingSimilar: 'Looking for possible duplicates',
    merge: 'Merge',
    mergeNamed: (name: string, code: string) => `Merge ${name} (${code})`,
    mergeAnother: 'Merge another doctor into this one',
    mergeTitle: (name: string) => `Merge into ${name}`,
    mergeChoose: (name: string, code: string) =>
      `Choose the doctor to merge into ${name} (${code}). Its cases move here and its record is removed.`,
    mergeDoctorLabel: 'Doctor to merge',
    casesOf: (name: string) => `Cases of ${name}`,
    noCases: 'No cases yet',
    noCasesOfDoctor: 'A case booked in for this doctor is listed here.',
    noCasesOfPatient: 'A case booked in for this patient is listed here.',
    doctorNotFound: 'Doctor not found',
    doctorNotFoundDetail: 'This doctor is not in the system. The record may have been deleted.',
    patientNotFound: 'Patient not found',
    patientNotFoundDetail: 'This patient is not in the system. The record may have been deleted.',
  },

  /**
   * The cases of a doctor or of a patient, and a doctor's patients, as the
   * tables on the record's own page. The request of 6 October 2026 (fifth),
   * section 4: search, filters, an order, a count, Reset filters, an empty
   * state with a next step, pages and an export, all asked of the server.
   */
  clientCases: {
    tabsLabel: 'The cases and the patients of this doctor',
    tabCases: 'Cases',
    tabPatients: 'Patients',

    // The cases.
    filtersLabel: 'Search and filter the cases',
    search: 'Search these cases',
    searchByPatient: 'Job order or patient',
    searchByDoctor: 'Job order or doctor',
    status: 'Status',
    allStatuses: 'All statuses',
    // Every case still in hand: In production, Ready to Deliver, On hold and Pending dispatch.
    activeOnly: 'Active only',
    archived: 'Archived',
    // The day the case arrived: Received, everywhere. The lab's instruction of 11 October 2026.
    created: 'Received',
    caseType: 'Case type',
    anyCaseType: 'Any case type',
    urgentOnly: 'Urgent only',
    due: 'Due',
    anyDue: 'Any due date',
    dueToday: 'Due today',
    overdue: 'Overdue',
    // On a patient's page, where the cases are several doctors'.
    doctor: 'Doctor',
    allDoctors: 'All doctors',
    // The orders, which the server sorts by.
    sortNewest: 'Newest first',
    sortOldest: 'Oldest first',
    sortDueSoonest: 'Due soonest',
    sortDueLatest: 'Due latest',
    sortStatus: 'Status',
    sortStatusLast: 'Status, last first',
    colCaseType: 'Case type',
    colDepartment: 'Department',
    colTechnician: 'Technician',
    colCreated: 'Received',
    colDue: 'Due',
    // A cell with nothing to say: a case that has left has no department, a delivered one no tray.
    none: '-',
    noneSpoken: 'None',
    casesCounted: (count: number) => (count === 1 ? 'case' : 'cases'),
    reset: 'Reset filters',
    // On a phone the filters are in a sheet, behind this button.
    filters: 'Filters',
    showCases: (count: number) => `Show ${String(count)} ${count === 1 ? 'case' : 'cases'}`,
    // The patient a doctor's cases are narrowed to, with the way to every patient's again.
    onlyPatient: 'Patient',
    everyPatient: 'Show every patient’s cases',
    noMatch: 'No case matches these filters',
    noMatchDetail: 'Reset the filters to see every case.',
    // The next step where a record has no case at all, for whoever may book one in.
    bookCase: 'Book in a case',
    exportCsv: 'Export',
    exporting: 'Exporting',
    casesExported: (count: number) =>
      `${String(count)} ${count === 1 ? 'case' : 'cases'} exported`,
    exportFailed: 'The export could not be produced',

    // A doctor's patients.
    patientFiltersLabel: 'Search and filter the patients',
    searchPatients: 'Search these patients',
    searchPatientsPlaceholder: 'Name or ID (PT-00031)',
    activePatients: 'Has active cases',
    sortName: 'Name A to Z',
    sortLatestCase: 'Latest case',
    sortMostCases: 'Most cases',
    colId: 'ID',
    colPatient: 'Patient',
    colCases: 'Cases',
    colActive: 'Active',
    colLastCase: 'Last case',
    patientsOf: (name: string) => `Patients of ${name}`,
    patientsCounted: (count: number) => (count === 1 ? 'patient' : 'patients'),
    showPatients: (count: number) =>
      `Show ${String(count)} ${count === 1 ? 'patient' : 'patients'}`,
    noPatientMatch: 'No patient matches these filters',
    noPatientMatchDetail: 'Reset the filters to see every patient of this doctor.',
    patientsExported: (count: number) =>
      `${String(count)} ${count === 1 ? 'patient' : 'patients'} exported`,
  },

  /**
   * A case opened where it is listed, over a doctor's or a patient's page. The
   * request of 6 October 2026 (fifth), section 3: a drawer at a desk, a page
   * over the screen on a phone, and a way to the case's own screen inside.
   */
  caseOverlay: {
    kicker: 'Case',
    back: 'Back',
    openInOrders: 'Open in Orders',
    openRecord: 'Open the record',
    notFound: 'This case could not be opened',
    notFoundDetail: 'It is not in the system. It may have been deleted for good.',
  },

  /** Where a case is delivered: the doctor's clinic, address and number. The same request, section 2. */
  deliverTo: {
    title: 'Deliver to',
    noAddress: 'No address on the doctor’s record yet.',
    nothing: 'The doctor’s record has no clinic, address or phone number yet.',
  },

  settings: {
    label: 'Admin',
    title: 'Settings',
    hint: 'How the production line runs, and where station staff may sign in. Changes apply immediately.',
    loading: 'Loading the settings',
    routingTitle: 'Production routing',
    routingHint: 'What happens when somebody marks a case done.',
    automatic: 'Automatic',
    automaticDetail:
      'Each case follows its own route: an impression goes to plaster first, a Zircon impression also through CAD / CAM, and a digital case to CAD / CAM first. Mark done sends it to the next station on that route.',
    manual: 'Choose at each station',
    manualDetail:
      'Every Mark done asks where the case goes next. The usual next station is already selected, so the common case is one tap.',
    saved: 'Production routing is now',
    savedAutomatic: 'automatic',
    savedManual: 'chosen at each station',
    conflict: 'Somebody else changed the settings while you were editing. They have been reloaded.',
    saveFailed: 'That change could not be saved.',
    locationTitle: 'Sign in location',
    // The end of the day, request of 25 September 2026.
    dayEndTitle: 'End of the day',
    dayEndHint:
      'Every clock still running stops at this time, on the lab’s clock, and the time worked until then is kept. A technician who forgot to press Pause or Done is not counted through the night, and Staff productivity counts no run past it.',
    dayEndLabel: 'Clocks stop at',
    dayEndOff: 'Clocks are never stopped for the day.',
    dayEndSave: 'Save',
    dayEndTurnOff: 'Turn off',
    dayEndTurnOn: 'Stop clocks at the end of the day',
    dayEndSaved: 'Clocks now stop at',
    dayEndSavedOff: 'Clocks are no longer stopped at the end of the day',
    dayEndInvalid: 'Give the time as HH:mm, for example 18:00.',
    dayEndUnknown: 'This version of the API does not carry the end of the day yet.',
    afterHoursLabel: 'Clocks started after that stop at',
    afterHoursHint:
      'A clock started after the end of the day stops at this time, or at the next end of the day if that comes first.',
    afterHoursOff: 'Clocks started after the end of the day run until the next end of the day.',
    afterHoursTurnOn: 'Stop them at midnight',
    afterHoursSave: 'Save the after-hours time',
    afterHoursTurnOff: 'Turn off the after-hours stop',
    afterHoursSaved: 'Clocks started after hours now stop at',
    afterHoursSavedOff: 'Clocks started after hours now run to the next end of the day',
    // Section 9 of the request of 5 October 2026.
    takeOverTitle: 'Allow take-over at Final Check',
    takeOverHint:
      'Whether somebody may take over a case that is at Final Check. A case that is Ready or in Logistics can never be taken over; whoever can assign work assigns it.',
    takeOverOff: 'Off',
    takeOverOffDetail: 'A case at Final Check cannot be taken over. It stays with whoever holds it.',
    takeOverOn: 'On',
    takeOverOnDetail:
      'A case at Final Check can be taken over, after a warning that names who holds it and who assigned it.',
    takeOverSavedOn: 'Take-over at Final Check is now allowed',
    takeOverSavedOff: 'Take-over at Final Check is now off',
  },
  // The installed app's icon and colours. Request of 5 October 2026.
  appIcon: {
    title: 'Installed app',
    hint: 'The icon and colours a phone uses when the app is added to its home screen.',
    current: 'Current icons',
    preview192: 'Icon',
    previewMaskable: 'Android, shaped',
    previewApple: 'iPhone',
    builtIn: 'The built-in Ceramica icons are in use.',
    themeColor: 'Theme colour',
    themeColorHint: 'The status bar and title bar of the installed app.',
    backgroundColor: 'Splash colour',
    backgroundColorHint: 'The screen shown while the app opens, and behind the shaped Android icon.',
    badColor: 'Give the colour as #rrggbb, for example #15130f.',
    saveColors: 'Save colours',
    resetColors: 'Use the default colours',
    colorsSaved: 'App colours saved',
    logo: 'Logo',
    logoHint:
      'A square PNG, JPEG, WebP or SVG of at least 512 × 512. Every size the phones ask for is made from it.',
    maskable: 'Shaped Android icon (optional)',
    maskableHint:
      'A 512 × 512 icon drawn edge to edge, with everything important inside the central circle. Without one, it is made from the logo on the splash colour.',
    chooseLogo: 'Choose a logo',
    chooseMaskable: 'Choose a shaped icon',
    clearMaskable: 'Make it from the logo',
    upload: 'Use these icons',
    uploading: 'Making the icons…',
    uploaded: 'App icons updated',
    reset: 'Use the built-in icons',
    resetDone: 'The built-in icons are back',
    unreadable: 'That picture could not be read.',
    updates:
      'A phone that already installed the app picks the new icon and colours up the next time Chrome checks, usually within a day of opening it. An iPhone keeps its icon until the app is removed from the home screen and added again.',
    insecure:
      'This page is not on a secure (HTTPS) address, so phones cannot install it as an app: Android adds a browser shortcut with a badge instead. Open the app at its HTTPS address to install it.',
  },
  users: {
    // Contact details, on Edit only. Request of 4 October 2026 (second), section 7.
    phone: 'Phone (optional)',
    phonePlaceholder: 'e.g. +971 50 123 4567',
    email: 'Email (optional)',
    emailPlaceholder: 'e.g. name@ceramica.ae',
    label: 'Admin',
    title: 'Users & permissions',
    hint: 'Create users and control what each role can see and do. Changes apply immediately.',

    tabsLabel: 'Users and permissions sections',
    tabTeam: 'Team',
    tabRevoked: 'Revoked',
    tabMatrix: 'Access matrix',

    team: 'Team',
    teamHint: 'Open a person to see every case they worked on.',
    revokedTitle: 'Revoked access',
    revokedHint:
      'These people can no longer sign in. Their work stays in the record; restoring lets them back in.',
    revokedEmpty: 'Nobody has been revoked',
    revokedEmptyDetail: 'Everyone on the team can sign in.',

    // The user record, reached by opening somebody in the team list.
    recordBack: 'Users',
    recordLabel: 'Work record',
    recordCases: 'cases worked',
    recordCasesOne: 'case worked',
    recordCaption: 'Cases worked',
    recordTabsLabel: 'Work record sections',
    recordTabCases: 'Cases',
    recordTabActivity: 'Activity log',
    recordLogEmpty: 'Nothing logged yet',
    recordLogEmptyDetail: 'This person has not changed anything in the system.',
    recordEmpty: 'No work recorded yet',
    recordEmptyDetail: 'Nothing has been logged against this person.',
    recordFiltersLabel: 'Filter the cases worked',
    recordSearch: 'Search these cases',
    recordSearchPlaceholder: 'Job order, doctor, patient or item',
    recordStage: 'Stage',
    recordDate: 'Date',
    recordTime: 'Active time',
    recordRunning: 'running',
    recordGone: 'Dispatched',
    recordProduct: 'Item',
    recordRework: 'Rework',
    recordPrev: 'Previous',
    recordNext: 'Next',
    recordPage: 'Page',
    recordOf: 'of',

    teamCount: 'users',
    teamCountOne: 'user',
    add: 'Add user',
    name: 'Name',
    username: 'User name',
    role: 'Role',
    department: 'Department',
    jobTitle: 'Title',
    access: 'Access',
    you: '(you)',
    active: 'Active',
    revoked: 'Revoked',
    revoke: 'Revoke access',
    // Asked before it happens: one stray tap on a phone used to lock somebody out.
    revokeTitle: 'Revoke access',
    revokeStatement: (name: string) =>
      `Revoke access for ${name}? They are signed out everywhere and cannot sign in until restored. Their work stays in the record.`,
    revokeConfirm: 'Revoke access',
    revokeCancel: 'Keep access',
    restore: 'Restore',
    // The rule, not a shrug: section 7.10 item 8 refuses it at the API too.
    cannotRevokeSelf: 'You cannot revoke your own access.',
    revokedToast: 'Access revoked for',
    revokedDetail: 'They can no longer sign in.',
    restoredToast: 'Access restored for',

    // A manager sets somebody else's password. Their own needs the current one.
    setPassword: 'Set password',
    setPasswordFor: 'Set password for',
    setPasswordTitle: 'New password for',
    setPasswordLead:
      'They are signed out on every device and sign in with this password next time.',
    newPassword: 'New password',
    repeatPassword: 'Type it again',
    passwordTooShort: 'Use at least eight characters',
    passwordsDiffer: 'The two passwords are not the same',
    passwordSet: 'New password set for',
    passwordSetDetail: 'They have been signed out everywhere.',

    addTitle: 'Add a user',
    addLead: 'The new user appears on the sign in screen and inherits the permissions of their role.',
    editTitle: 'Edit',
    editLead: 'Changes apply on their next action, without signing them out. Their password is changed from Set password.',
    edit: 'Edit',
    editFor: 'Edit',
    save: 'Save changes',
    saved: 'updated.',
    fullName: 'Full name',
    namePlaceholder: 'e.g. Omar Haddad',
    usernamePlaceholder: 'auto from the name',
    titlePlaceholder: 'e.g. Ceramic technician',
    password: 'Password',
    passwordHint: 'At least eight characters. They can change it after signing in.',
    cancel: 'Cancel',
    create: 'Create user',
    creating: 'Creating',
    created: 'created. They can now sign in as',
    intakeOperator: 'Also does intake',
    intakeOperatorHint: 'CAD / CAM staff who create job orders at reception.',
    /*
     * A department supervisor. Section 11 of the request of 20 September 2026: a
     * technician who also approves Final Check. A flag, not a role, so their home
     * screen, menu and tab bar are unchanged.
     */
    supervisor: 'Department supervisor',
    supervisorHint: 'Also approves Final Check, for their own department and every other.',
    supervisorOf: 'Supervisor',

    matrix: 'Access matrix',
    matrixTitle: 'What each role can see and do',
    matrixHint: 'Tick a box to grant, untick to revoke. Users & permissions stays locked to Management.',
    matrixExceptions: "Exceptions for individual people are set on each person's record.",
    screens: 'Screens',
    actions: 'Actions',
    permission: 'Permission',
    granted: 'Granted to',
    revokedFrom: 'Revoked from',
    locked: 'locked',
    lockedHint: 'Locked. Removing this would lock every user out of the system.',
    saving: 'Saving',
    saveFailed: 'That change could not be saved.',
    conflict: 'Somebody else changed the matrix while you were editing. It has been reloaded.',
  },

  // Access per person, on top of access per role. Request of 13 September 2026, section 2.
  access: {
    title: 'Special access',
    lead: 'Exceptions for this person only, on top of what their role holds. A denial wins over the role.',
    takesEffect: 'Takes effect the next time they sign in or their session refreshes.',
    inherit: 'Inherit',
    grant: 'Grant',
    deny: 'Deny',
    noteLabel: 'Why (optional)',
    notePlaceholder: 'e.g. Covers Final Check on Fridays',
    save: 'Save access',
    saving: 'Saving',
    saved: 'Access saved for',
    pill: 'Special access',
    is: 'is',
    roleHolds: 'already holds',
    roleHoldsNothing: 'holds no permissions of its own.',
    and: 'and',
    permissions: 'Permissions',
    screens: 'Screens',
    fromRoleYes: 'holds this',
    fromRoleNo: 'does not hold this',
    lastChanged: 'Last changed by',
    on: 'on',
    loading: 'Loading special access',
    // The lab's request of 11 October 2026: what was given and will not work, and a screen that says what it needs.
    gapsTitle: 'Given, and will not work as it stands',
    gapsLead: 'Each of these rests on something that is not given. Give that as well, or take this away.',
    screenClosed: 'This screen needs more access',
    // The server's refusals, worded once in the domain.
    forbidden: USER_ACCESS_REFUSAL.forbidden,
    ownAccess: USER_ACCESS_REFUSAL.ownAccess,
    stale: USER_ACCESS_REFUSAL.stale,
    lastAdministrator: USER_ACCESS_REFUSAL.lastAdministrator,
    grantedAndDenied: USER_ACCESS_REFUSAL.grantedAndDenied,
    screenGrantedAndDenied: USER_ACCESS_REFUSAL.screenGrantedAndDenied,
  },

  geofence: {
    label: 'Access',
    title: 'Station sign-in by location',
    hint: 'Refuse a sign in from outside the lab, for the roles that work at a station.',
    enable: 'Refuse station sign-in from outside the radius',
    latitude: 'Latitude',
    longitude: 'Longitude',
    radius: 'Radius in metres',
    appliesTo: 'Applies to',
    useHere: 'Use my current location',
    save: 'Save',
    saving: 'Saving',
    saved: 'Station sign-in updated.',
    locationTaken: 'Coordinates taken from this device.',
    locationRefused: 'This device would not give its location.',
    // Said plainly on the screen, because a manager who believes this is a
    // lock would make worse decisions than one who knows it is a fence.
    advisory:
      'This is checked in the browser, so it prevents an accidental sign-in from outside rather than a determined one. Ask the backend developer to enforce it on the server as well.',
  },

  /*
   * The notification centre, the Notifications section of the home screen, and
   * the settings of both. The request of 7 October 2026, sections 1 and 2.
   */
  notifications: {
    title: 'Notifications',
    unread: 'unread',
    unreadOne: 'unread',
    allRead: 'Everything here has been read.',
    markAll: 'Mark all as read',
    close: 'Close',
    empty: 'Nothing to report',
    emptyDetail: 'What happens to your cases appears here: assignments, rework, messages and the rest.',
    emptyFiltered: 'Nothing here',
    emptyFilteredDetail: 'No notification matches what is chosen above.',
    // The tags over the list.
    tagsLabel: 'Show',
    all: 'All',
    unreadTag: 'Unread',
    order: 'Order',
    newestFirst: 'Newest first',
    oldestFirst: 'Oldest first',
    today: 'Today',
    yesterday: 'Yesterday',
    unreadMark: 'Not read yet',
    settings: 'Notification settings',
    // What the button reads, inside a dialog that is already called Notifications.
    settingsShort: 'Settings',
    /*
     * The moving line of the notifications management marked mandatory, while
     * they are unread. The request of 8 October 2026 (fourth).
     */
    ticker: {
      label: 'Important notifications',
      badge: 'Important',
      pause: 'Pause the moving line',
    },
    // The section of the home screen.
    home: {
      title: 'Notifications',
      viewAll: 'View all',
      choose: 'Choose what shows here',
      empty: 'Nothing new for you.',
      listLabel: 'Your latest notifications',
      // Taking the section off a screen, and the way back. The lab's answer of 12 October 2026.
      hide: 'Take notifications off this screen',
      hidden: 'Notifications are off this screen. They are still under the bell.',
      putBack: 'Put back',
    },
    // A person's own settings.
    mine: {
      title: 'Your notifications',
      lead: 'Turn off what you do not need, and choose what your home screen shows. Kept on your account.',
      on: 'On',
      onHome: 'On home screen',
      alwaysOn: 'Always on',
      inApp: 'In the app',
      push: 'Push',
      none: 'Your role receives no notifications.',
      saved: 'Notification settings saved',
      // The whole section on a person's first screen, on or off.
      homeSection: 'Home screen',
      showOnHome: 'Show my latest notifications on my home screen',
      showOnHomeAbout: 'Your latest five, on the first screen you see. Off, they are still under the bell.',
      done: 'Done',
      // What the device itself shows.
      device: 'This device',
      deviceLead: 'Push notifications are shown by this device while Ceramica is open or in the background.',
      deviceAllow: 'Allow on this device',
      deviceAllowed: 'Allowed on this device',
      deviceBlocked: 'Blocked in this browser. Allow notifications for this site in its settings.',
      deviceUnsupported: 'This browser cannot show notifications.',
    },
    // Management's settings, for a role and for one person.
    admin: {
      tab: 'Notifications',
      title: 'Who is notified of what',
      hint: 'For each role: which notifications it receives, where they appear, and which nobody in it may turn off.',
      role: 'Role',
      receives: 'Receives',
      inApp: 'In the app',
      push: 'Push',
      mandatory: 'Mandatory',
      changedMark: 'Changed from the default',
      ownMark: "Different from the role's",
      save: 'Save changes',
      reset: 'Reset to defaults',
      resetUser: 'Reset to role defaults',
      nothingToSave: 'Nothing changed',
      confirmSave: (count: number, who: string) =>
        count === 1
          ? `Save 1 change to the notifications of ${who}?`
          : `Save ${String(count)} changes to the notifications of ${who}?`,
      confirmReset: (who: string) => `Put the notifications of ${who} back to the defaults?`,
      confirmResetUser: (who: string, role: string) =>
        `Reset the notifications of ${who} to those of ${role}? Every exception made for them is taken off.`,
      saved: (who: string) => `Notifications of ${who} saved`,
      wasReset: (who: string) => `Notifications of ${who} reset`,
      userTitle: 'Notifications',
      userHint: (role: string) =>
        `What this person is notified of. It follows ${role} unless you change it here.`,
      loading: 'Loading the notification settings',
      noChannel: 'Choose where it appears: in the app, as a push notification, or both.',
    },
  },

  update: {
    // A newer build is on the server. Offered, never forced: the reload waits
    // for the tap, so nobody loses a form they are half way through.
    message: 'A new version of Ceramica is available.',
    // Naming it, when the server says which release it has and it is not the
    // one loaded. The request of 5 October 2026 (third), section 8.
    messageFor: (label: string) => `Ceramica ${label} is available.`,
    action: 'Update',
    applying: 'Updating',
    offline: 'Could not reach the server. Try again when the connection is back.',
  },

  sync: {
    // Section 9. Quiet. The work is safe and saying so is the whole job of
    // this line; a red banner would tell a technician to stop working.
    offline: "You're offline. Your work is saved and will sync when you're back.",
    queued: 'Saved. It will sync when the connection is back.',
    // Beside an entry the server answered with a refusal and no sentence of its own.
    refused: 'The server did not accept this. Open the case and check it.',
    trayLabel: 'Not synced',
    title: 'Not synced',
    leadPending: 'Waiting for a connection. Nothing is lost.',
    leadFailed: 'The server refused these. They need somebody to look.',
    pending: 'Waiting to sync',
    failed: 'Refused by the server',
    retry: 'Try again now',
    retrying: 'Trying',
    discard: 'Discard',
    discarded: 'Discarded. It will not be sent.',
    close: 'Close',
    attempts: 'attempts',
    allClear: 'Everything is synced.',
    syncedNow: 'Synced.',
    nothingSent: 'Still no connection. Your work is still saved.',
  },

  install: {
    title: 'Install Ceramica',
    lead: 'Add Ceramica to your home screen. It opens full screen, like an app, and keeps working when the Wi-Fi drops.',
    accept: 'Install',
    dismiss: 'Not now',
    // What each kind of phone browser is told. Request of 6 October 2026.
    steps: {
      none: [],
      prompt: ['Tap Install below.', 'Open Ceramica from your home screen from now on.'],
      chromeMenu: [
        'Tap the three dots at the top of Google Chrome.',
        'Tap "Add to Home screen", then "Install".',
        'Open Ceramica from your home screen from now on.',
      ],
      openChrome: [
        'This browser cannot install the app. Open Google Chrome.',
        'Go to this same address in Google Chrome and sign in.',
        'Tap Install when Chrome offers it.',
      ],
      ios: [
        'Tap the Share button in Google Chrome or Safari.',
        'Tap "Add to Home Screen", then "Add".',
        'Open Ceramica from your home screen from now on.',
      ],
    } satisfies Record<string, readonly string[]>,
    iconChanged: 'Ceramica has a new app icon.',
    iconChangedHow:
      'An iPhone keeps the old icon. To get the new one, remove Ceramica from your home screen, open this address in the browser and add it to the home screen again.',
  },

  line: {
    label: 'Live floor',
    title: 'Production line',
    hint: 'Every stage in one line. Tap a stage to see its kits.',
    liveFloor: 'Live floor',
    onTheLine: 'kits on the line · flagged cases can jump to any stage',
    /**
     * The button that stacks the stages top to bottom. It reads the same pressed
     * or not: the pressed state says which way the line is drawn. The request of
     * 5 October 2026 (third), section 9.
     */
    verticalView: 'Vertical view',
    kits: 'kits',
    ready: 'ready',
    // The client's own words for the ready column.
    awaitingDispatch: 'awaiting dispatch',
    // The eighth card. Section 2 of the request of 13 September 2026.
    logistics: 'Logistics',
    logisticsOrders: 'orders',
    logisticsWaiting: 'waiting for a courier',
    logisticsAwaitingWaybill: 'awaiting a waybill',
    logisticsInTransit: 'on the way',
    logisticsHeld: 'on hold',
    logisticsEmpty: 'nothing on its way',
    logisticsLabel: 'Out of the lab',
    logisticsTitle: 'Logistics',
    logisticsHint: 'Cases past Ready that have not arrived. The same list the dispatch desk works from.',
    logisticsCaption: 'Cases in logistics',
    logisticsEmptyHeading: 'Nothing is on its way',
    logisticsEmptyDetail: 'Cases appear here once they are paid, dispatched or held after Ready.',
    logisticsColDispatched: 'Left the lab',
    overdue: 'overdue',
    /** What a card says when nothing on it is late, in place of a capacity. */
    onTime: 'all on time',
    note: 'Flagged and failed cases can be sent to any stage. The line shows where everything is right now.',
  },

  stage: {
    back: 'Production line',
    label: 'Stage',
    kit: 'kit',
    kits: 'kits',
    empty: 'No kits in this stage',
    emptyDetail: 'Nothing is here at the moment.',
    statKits: 'Kits',
    statOverdue: 'Overdue',
    statOldest: 'Oldest wait',
    working: 'Working now',
    colItem: 'Item and teeth',
    colInput: 'Input',
    colInStage: 'In stage',
    colAssigned: 'Assigned',
    colPhotos: 'Photos',
    unassigned: 'Unassigned',
    // For a row whose name is ever missing: a word, never an id.
    assigned: 'Assigned',
    // The filter bar over the kits at a stage, and over Final Check.
    filtersLabel: 'Filter the cases at this stage',
    search: 'Search these cases',
    searchPlaceholder: 'Job order, kit, doctor or patient',
    input: 'Input',
    allInputs: 'All inputs',
    urgentOnly: 'Urgent only',
    overdueOnly: 'Overdue only',
    reworkOnly: 'Rework only',
    assignedTo: 'Assigned to',
    anyone: 'Anyone',
  },

  overview: {
    label: 'Today',
    title: 'Floor overview',
    // On a figure that opens its list. The request of 7 October 2026, section 7.
    openCounted: 'Open the cases this counts',
    figuresLabel: 'The lab in numbers',
    rangeLabel: 'Reporting range',
    rightNow: 'Right now',
    liveWork: 'Live work',
    liveWorkHint: 'Technicians actively working, tracked from scan.',
    noLiveWork: 'Nobody is timing a task right now',
    noLiveWorkDetail: 'Work appears here the moment somebody scans a tray.',
    action: 'Action',
    attention: 'Needs attention',
    attentionHint: 'Urgent, overdue or in rework. Tap to open.',
    allClear: 'Nothing needs attention',
    allClearDetail: 'No urgent, overdue or rework cases on the floor.',
    reasonUrgent: 'Urgent',
    reasonOverdue: 'Overdue',
    reasonRework: 'Rework',
    people: 'People',
    team: 'Team today',
    teamHint: 'Time logged per technician.',
    viewAll: 'View all',
    reporting: 'Reporting',
    analytics: 'Analytics',
    openReports: 'Open reports',
    // A case worked twice is one case. The lab's request of 17 October 2026, item 6.
    tasksDone: 'Cases done',
    timeWorked: 'Time worked',
    averagePerTask: 'Avg / case',
    dueIn: 'due in',
    daysShort: 'd',
    overdueBy: 'overdue by',
    /*
     * A clock running longer than a working day, section 1.3 of the request of
     * 20 September 2026, and the button that stops it, request of 25 September
     * 2026: the clock runs until Pause or Done, so a forgotten one runs all
     * night, and this is where the next person to look notices.
     */
    overrunning: 'Running longer than a working day',
    stop: 'Stop clock',
    stopTitle: 'Stop the clock on',
    stopLead:
      'The time so far is kept on their record, and the case stays where it is. They can scan the tray to carry on.',
    stopConfirm: 'Stop the clock',
    stopCancel: 'Keep it running',
    stopped: 'clock stopped after',
    stopFailed: 'That clock could not be stopped.',
  },

  /*
   * The Dashboard, and what makes it and the Overview a person's own: a screen
   * of widgets they add to, take from and put in their own order. The lab's
   * request of 12 October 2026.
   */
  dashboard: {
    label: 'Today',
    title: 'Your dashboard',
    // The sheet that arranges a screen of widgets: the words of Customize columns, said of a screen.
    customize: {
      open: 'Customize',
      title: 'Customize this screen',
      lead: 'Tick what this screen shows, and drag it into the order you want it in. Anything without a tick is one more thing it can show.',
      listLabel: 'What this screen shows, in your order',
      alwaysShown: 'Always shown',
      drag: (widget: string) => `Drag ${widget} to another place`,
      moveUp: (widget: string) => `Move ${widget} up`,
      moveDown: (widget: string) => `Move ${widget} down`,
      rowsPerPage: 'Rows per page',
      reset: 'Reset to default',
      done: 'Done',
      close: 'Close',
      saved: 'Saved to your account as you go, and the same on every device.',
    },
    emptyHeading: 'Nothing on this screen',
    emptyDetail: 'Customize puts back whatever you want to see here.',
    /** Said when a widget is taken off from the widget itself. */
    takenOff: (widget: string) => `${widget} is off this screen. Customize puts it back.`,
    viewAll: 'View all',
    /** On a list that shows the first few of more. */
    more: (shown: number, total: number) => `Showing ${String(shown)} of ${String(total)}`,
    // The small word over a widget's title.
    kicker: {
      you: 'For you',
      goTo: 'Go to',
      cases: 'Cases',
      dispatch: 'Dispatch',
      hold: 'On hold',
      money: 'Money',
    },
    // Each widget as the Customize list names it, with what it shows.
    widgets: {
      line: { title: 'Production line', about: 'Every department, with the cases at it.' },
      notifications: { title: 'Notifications', about: 'Your latest five, and the way to all of them.' },
      figures: {
        title: 'The lab in numbers',
        about: 'Active, due, overdue, on time, turnaround and completed.',
      },
      sales: { title: 'Analytics', about: 'Units sold and the best selling item.' },
      liveWork: { title: 'Live work', about: 'Who is timing a task right now.' },
      attention: { title: 'Needs attention', about: 'Urgent, overdue and rework cases on the floor.' },
      team: { title: 'Team today', about: 'Time logged by each technician.' },
      shortcuts: { title: 'Your screens', about: 'Every screen you have, one press away.' },
      caseCounts: {
        title: 'Cases in hand',
        about: 'How many are active, due today, overdue, urgent and on hold.',
      },
      dueToday: { title: 'Due today', about: 'The cases to deliver today.' },
      overdue: { title: 'Overdue', about: 'The cases past their delivery date.' },
      urgent: { title: 'Urgent', about: 'Every case marked urgent.' },
      latestCases: { title: 'Latest received', about: 'The newest cases booked in.' },
      readyToShip: { title: 'Waiting for dispatch', about: 'Finished and paid, ready to be sent.' },
      onHold: { title: 'Cases on hold', about: 'The cases the lab has stopped, and why.' },
      moneyOwed: {
        title: 'What the lab is owed',
        about: 'Outstanding, unpaid cases, part paid and credit held.',
      },
      toBePaid: { title: 'To be paid', about: 'The cases still to be paid.' },
      partPaid: { title: 'Part paid', about: 'Cases with some of the bill received and not all of it.' },
      changedCases: {
        title: 'Changed since priced',
        about: 'Edited after pricing, waiting for Accounts to look again.',
      },
      owedByDoctor: { title: 'Owed by doctor', about: 'The doctors who owe the most.' },
      // The lab's request of 17 October 2026, items 8 and 14.
      released: { title: 'Released, not paid', about: 'Cases let go to be shipped before they were paid.' },
      inCeramic: { title: 'In ceramic', about: 'Every case at Ceramic now, assigned or not.' },
    },
    counts: {
      label: 'Cases in numbers',
      // Still being made: a case in Ready or Logistics is not active. The lab's request of 17 October 2026, item 3.
      active: 'Active',
      dueToday: 'Due today',
      overdue: 'Overdue',
      urgent: 'Urgent',
      onHold: 'On hold',
    },
    // What a list says when it has nothing in it.
    empty: {
      dueToday: 'Nothing is due today.',
      overdue: 'Nothing is overdue.',
      urgent: 'No case is marked urgent.',
      latestCases: 'No case has been booked in yet.',
      readyToShip: 'Nothing is waiting for dispatch.',
      onHold: 'No case is on hold.',
      toBePaid: 'Every case is paid.',
      partPaid: 'No case is paid in part.',
      changedCases: 'Nothing has changed since it was priced.',
      owedByDoctor: 'No doctor owes anything.',
      released: 'No case is released and still unpaid.',
      inCeramic: 'No case is at Ceramic.',
    },
    urgentMark: 'Urgent',
    casesOwing: (count: number) => `${String(count)} ${count === 1 ? 'case' : 'cases'}`,
  },

  /*
   * The system's money and the dollar rate. The lab's request of
   * 17 October 2026, item 13.
   */
  currency: {
    label: 'Amounts in',
    aed: 'AED',
    usd: 'USD',
    rate: (rate: string) => `1 USD = ${rate} AED`,
    setBy: (name: string | null, day: string) => (name === null ? `set on ${day}` : `set by ${name} on ${day}`),
    noRate: 'No dollar rate is set, so amounts are shown in dirhams only.',
    setRate: 'Set the dollar rate',
    changeRate: 'Change the rate',
    rateTitle: 'Dollar rate',
    rateLead:
      'How many dirhams one dollar buys. Every amount stays in dirhams: the rate is only used to show amounts in dollars.',
    rateLabel: 'Dirhams for one dollar',
    saveRate: 'Save the rate',
    saved: (rate: string) => `Dollar rate set: 1 USD = ${rate} AED`,
    badRate: EXCHANGE_RATE_REFUSAL.badRate,
    cancel: 'Cancel',
  },

  /* A doctor's own prices. The lab's request of 17 October 2026, item 11. */
  doctorPrices: {
    tab: 'Prices',
    heading: 'Prices for this doctor',
    lead: 'A price set here is what this doctor is charged for the item on their next cases. Cases already taken in keep their prices. An item with no price here is charged at the list price.',
    loading: 'Reading the prices',
    empty: 'The price list has no items yet.',
    listLabel: (name: string) => `The prices for ${name}`,
    listPrice: (price: string) => `list price ${price}`,
    setOn: (day: string) => `set on ${day}`,
    theirPrice: 'Their price',
    retired: 'retired',
    save: 'Save',
    useList: 'Use list price',
    saved: (item: string, price: string) => `${item} is now ${price} for this doctor`,
    cleared: (item: string) => `${item} is back to the list price for this doctor`,
    badPrice: DOCTOR_PRICE_REFUSAL.badPrice,
  },

  /* Mark paid shows a case as an invoice. The lab's request of 17 October 2026, item 12. */
  invoice: {
    title: 'Invoice',
    number: 'Invoice no.',
    date: 'Date',
    serialNumber: 'Job order serial no.',
    received: 'Received',
    due: 'Due',
    billTo: 'Bill to',
    patient: 'Patient',
    noDoctor: 'No doctor record',
    print: 'Print',
    confirmRelease: 'Yes, release',
    released: (name: string | null, day: string) =>
      name === null
        ? `Released to be shipped unpaid on ${day}.`
        : `Released to be shipped unpaid by ${name} on ${day}.`,
    inDirhams: (currency: string) => `All amounts in ${currency}.`,
    inDollarsAt: (rate: string) => `Amounts shown in USD at 1 USD = ${rate} AED. The invoice is kept in AED.`,
  },

  /* The number on the paper job order handed to the doctor. The lab's request of 17 October 2026, item 7. */
  serialNumber: {
    label: 'Job order serial number',
    optional: 'optional',
    helper: 'The job order on papers handed to doctors.',
    short: 'Serial no.',
  },

  /* The admin's figures over the Production line and Orders. The lab's request of 17 October 2026, item 5. */
  adminFigures: {
    label: 'Cases in figures',
    active: 'Active cases',
    urgent: 'Urgent',
    overdue: 'Overdue',
    held: 'On hold',
    hide: 'Hide these figures',
    showAgain: 'Show the figures',
    takenOff: 'The figures are off this screen. Show the figures puts them back.',
  },

  staff: {
    label: 'People',
    title: 'Staff productivity',
    hint: 'Time logged per task, from scan to completion, the teeth worked on, and the job orders each person created.',
    colTechnician: 'Staff member',
    colStation: 'Station',
    // A case worked twice is one case, and its teeth count once. The lab's request of 17 October 2026, item 6.
    colTasks: 'Cases',
    colTeeth: 'Teeth',
    colCreated: 'Orders created',
    notAtBench: 'Not at a tray',
    colTime: 'Time worked',
    colAverage: 'Avg / case',
    colOnTime: 'On time',
    colUtilisation: 'Utilisation',
    /* The exact period, below the three ranges. Request of 4 October 2026. */
    customDate: 'Custom date',
    customTitle: 'Choose the period',
    customLead: 'Both days count, on the lab\u2019s calendar. Leave the end empty for a single day.',
    customFrom: 'From',
    customTo: 'To',
    customApply: 'Show this period',
    customClear: 'Back to today',
    customNeedsDay: 'Choose at least one day.',
    sortBy: 'Sort by',
    openProfile: 'Open profile',
    empty: 'Nobody has logged time in this range',
    emptyDetail: 'Widen the range, or wait for the first scan of the shift.',
  },

  perf: {
    label: 'Me',
    hint: 'Your own record. Nobody else on the floor sees it.',
    tasksDone: 'Cases done',
    teethWorked: 'Teeth worked',
    jobOrdersCreated: 'Job orders created',
    timeWorked: 'Time worked',
    averagePerTask: 'Avg / case',
    onTime: 'On time',
    utilisation: 'Shift utilisation',
    ofShift: 'logged of an eight hour shift',
    empty: 'Nothing logged in this period',
    emptyDetail: 'Complete a task from your tray to start building your record.',
  },

} as const;

/**
 * The sidebar heading above the menu, which names what the role is looking at
 * rather than repeating the word Menu at everybody.
 */
export const SIDEBAR_HEADING: Record<Role, string> = {
  management: 'Floor',
  reception: 'Reception & Logistics',
  accountant: 'Accounts',
  cadcam: 'My queue',
  tech: 'My queue',
};

/**
 * Menu labels that differ from the canonical view label, because a sidebar
 * column is narrower than a screen heading. From the prototype's NAVLABEL.
 */
export const NAV_LABEL_OVERRIDE: Partial<Record<ViewKey, string>> = {
  reports: 'Reports',
  shipping: 'Shipping',
  users: 'Users',
};

/**
 * Shorter still for the mobile tab bar, where five items share the width of a
 * phone. From the prototype's SHORT.
 */
export const TAB_LABEL_OVERRIDE: Partial<Record<ViewKey, string>> = {
  dashboard: 'Home',
  line: 'Line',
  intake: 'New',
  finalcheck: 'QC',
  perf: 'Stats',
  queue: 'Queue',
  reports: 'Reports',
  shipping: 'Shipping',
  users: 'Users',
};

/** The top bar title per screen. From the prototype's renderTop. */
export const SCREEN_TITLE: Record<ViewKey, string> = {
  overview: 'Overview',
  dashboard: 'Dashboard',
  line: 'Production line',
  orders: 'Orders',
  finalcheck: 'Final Check',
  reports: 'Reports',
  archive: 'Archive & search',
  activity: 'Activity log',
  staff: 'Staff productivity',
  users: 'Users & permissions',
  settings: 'Settings',
  items: 'Items',
  intake: 'New job order',
  shipping: 'Shipping & dispatch',
  payments: 'Payments',
  onhold: 'On Hold Cases',
  // The request of 6 October 2026 (second), item 4.
  doctors: 'Doctors',
  patients: 'Patients',
  queue: 'Queue',
  perf: 'My performance',
  stage: 'Stage',
  job: 'Job order',
  record: 'Case record',
  scan: 'Scan a tray',
};
